import type { TranslationKeys } from "../types.js";
import { marketing as marketingEn } from "./marketing/en.js";

export const en: TranslationKeys = {
  common: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    update: "Update",
    search: "Search",
    filter: "Filter",
    actions: "Actions",
    back: "Back",
    next: "Next",
    previous: "Previous",
    close: "Close",
    description: "Optimized for SEO with Server-Side Rendering (SSR) and Server Components",
    locale: {
      label: "Interface language",
      hint: "Dashboard copy and Accept-Language sent to the API.",
      es: "Spanish",
      en: "English",
      fr: "French",
      pt: "Portuguese",
    },
    theme: {
      label: "Appearance",
      light: "Light",
      dark: "Dark",
      system: "System",
    },
  },
  auth: {
    login: "Sign in",
    logout: "Sign out",
    register: "Sign up",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    rememberMe: "Remember me",
    welcome: "Welcome",
    welcomeBack: "Welcome back",
  },
  email: {
    welcome: {
      subject: "Welcome to our platform!",
      title: "Hello {{name}}!",
      content: "Thank you for joining our platform. We're excited to have you here.",
      buttonText: "Get started",
    },
    verification: {
      subject: "Verify your email",
      title: "Verify your email",
      content: "Click on the following link to verify your email address:",
      buttonText: "Verify email",
      expiresIn: "This link will expire in 24 hours.",
    },
    passwordReset: {
      subject: "Reset your password",
      title: "Password recovery",
      content: "Click on the following link to reset your password:",
      buttonText: "Reset password",
      expiresIn: "This link will expire in 1 hour.",
      ignore: "If you didn't request this change, please ignore this email.",
    },
    houseInvitation: {
      subject: "You are invited to join a household on HomeExpense",
      title: "Household invitation",
      greeting: "{{inviterName}} has invited you to join “{{houseName}}” on HomeExpense.",
      content:
        "Create your account or sign in with this email to accept the invitation and start sharing expenses.",
      buttonRegister: "Create account and join",
      loginHint: "Already have an account?",
      loginCta: "Sign in with this link",
      expiresIn: "This invitation expires in 14 days.",
      footer: "If you were not expecting this email, you can ignore it.",
    },
  },
  errors: {
    generic: "An error occurred",
    notFound: "Not found",
    unauthorized: "Unauthorized",
    forbidden: "Forbidden",
    validation: "Validation error",
    serverError: "Server error",
    networkError: "Network error",
  },
  validation: {
    required: "This field is required",
    email: "Invalid email",
    minLength: "Must be at least {{min}} characters",
    maxLength: "Cannot exceed {{max}} characters",
    passwordMismatch: "Passwords do not match",
  },
  dashboardUi: {
    shell: {
      brandTitle: "HomeExpense",
      brandTagline: "Clear household expense management",
      brandSlogan: "Building your family's future,\none expense at a time.",
      addExpense: "Add expense",
      searchPlaceholder: "Search HomeExpense…",
      userStubName: "User",
      userStubRole: "Member",
      notificationsComingSoon: "Notifications (coming soon)",
      noActiveHouse: "No house selected",
      openingBalanceLine: "Opening balance: {{amount}}",
    },
    nav: {
      mainNavigation: "Main navigation",
      dashboard: "Dashboard",
      journal: "Journal",
      search: "Search",
      budget: "Budget",
      analytics: "Insights",
      vault: "Vault",
      scan: "AI scan",
      financings: "Financing",
      houses: "Houses",
      profile: "Profile",
      accountData: "Account & data",
      settings: "Settings",
      support: "Support",
    },
    placeholder: {
      hint: "Screen under construction (UI phase). API contract: docs/REQUISITOS_HOMEEXPENSE.md §14.",
    },
    pages: {
      home: "Dashboard home",
      login: "Sign in",
      register: "Sign up",
      forgotPassword: "Forgot password",
      resetPassword: "Reset password",
      settingsPassword: "Change password",
      settingsPasskeys: "Passkeys",
      settingsProfile: "User profile",
      settingsPersonal: "Personal details",
      settings: "Settings",
      support: "Support",
      houses: "My houses",
      houseMembers: "House members",
      expenses: "Expense journal",
      financings: "Financing",
      financingNew: "New financing",
      financingDetail: "Financing details",
      financingEdit: "Edit financing",
      expenseNew: "New expense",
      expenseDetail: "Expense detail",
      expensePaidFeedback: "Expense paid",
      budget: "Monthly budget",
      vault: "Warranty vault",
      vaultNew: "Register asset",
      vaultDetail: "Asset detail",
      scan: "AI scan",
      houseAnalytics: "Household insights",
      search: "Search",
      notifications: "Notifications",
      notificationSettings: "Notification settings",
      settingsDevices: "Devices and sessions",
      onboarding: "Initial setup",
    },
    searchPage: {
      subtitle: "Find expenses and vault items in your active house.",
      backHome: "Back to home",
      noActiveHouse: "Choose an active house to search its journal and vault.",
      minCharsHint: "Type at least 2 characters to search.",
      emptyPrompt:
        "Use the field above to search expense titles, notes or categories, or vault names and notes.",
      resultsFor: "Results for “{{query}}”",
      openJournalWithQuery: "Open full journal",
      noResults: "No matches. Try different words or use filters in the journal.",
      sectionExpenses: "Expenses",
      sectionVault: "Vault",
    },
    states: {
      loadingHint: "Loading data…",
      emptyTitle: "Nothing here yet",
      emptyHint: "When there is data, it will show up here.",
      errorTitle: "Something went wrong",
      errorHint: "Try again or check your connection.",
      retry: "Retry",
    },
    mobile: {
      openMenu: "Open menu",
      homeLink: "Home",
    },
    analytics: {
      openFromHome: "View spending insights (last months)",
      openFromBudget: "Open household insights and charts",
      backToBudget: "Back to budget",
      missingHouse: "Select a house to see analytics.",
      subtitle: "Paid spending vs planned budget by month, category trends, and budget hints (last {{count}} months).",
      empty: "No paid expenses in this period yet. Once you record spending, charts and insights will appear here.",
      chartTitle: "Paid vs planned by month",
      chartPaid: "Paid",
      chartPlanned: "Planned",
      chartAvgLine: "Average paid (months with spending)",
      peakMonthTitle: "Highest-spend month",
      peakMonthBody: "{{month}} — {{amount}} paid",
      peakMonthNone: "No spending recorded in this window.",
      avgSpendTitle: "Average paid per active month",
      avgSpendHint: "Only months with paid spending > 0 are included.",
      topCategoriesTitle: "Top accumulated spending by category (last {{count}} months)",
      otherCategories: "All other categories combined: {{amount}}",
      overspendLeaderTitle: "Largest cumulative overrun vs plan (last {{count}} months)",
      overspendLeaderBody:
        "“{{name}}” is {{amount}} over plan cumulatively in months with a budget line, counting only the last {{count}} months.",
      reviewTitle: "Categories to review",
      reviewHint: "Often above plan in months with a budget line — consider raising the plan or cutting spend.",
      reviewEmpty: "No category matches this pattern in the selected window.",
      reduceTitle: "Room to lower the plan",
      reduceHint: "Often below plan — you may safely reduce the monthly allocation.",
      reduceEmpty: "No category matches this pattern in the selected window.",
    },
    onboarding: {
      pageTitle: "Set up your space in three steps",
      progress: "Step {{current}} of {{total}}",
      stepWelcomeTitle: "Welcome to HomeExpense",
      stepWelcomeBody:
        "We will guide you to create or confirm your home, invite people who share expenses, and set a monthly cap as a reference. You can skip at any time.",
      stepHouseTitle: "Home name",
      stepHouseBody: "This is where you will group expenses, budget, and members.",
      stepHouseHasHouseTitle: "Your home",
      stepHouseHasHouseBody:
        'You are already part of “{{name}}”. You can continue or create another home from “Homes” whenever you like.',
      stepMembersTitle: "Who shares the home",
      stepMembersBody:
        "Invite partners or flatmates by email. You can also continue alone and send invitations later.",
      inviteEmailLabel: "Email to invite",
      inviteEmailPlaceholder: "name@example.com",
      inviteSend: "Send invitation",
      inviteSending: "Sending…",
      inviteSent: "Invitation sent.",
      inviteSkipHint: "You can invite later from House members.",
      stepBudgetTitle: "Monthly cap (reference)",
      stepBudgetBody:
        "A soft monthly limit helps the dashboard show spending pace. You can change it in Budget.",
      budgetAmountLabel: "Monthly amount ({{currency}})",
      budgetSkipHint: "You can leave this empty and configure it later.",
      stepDoneTitle: "All set",
      stepDoneBody: "You can use the dashboard now. Want a quick interface tour?",
      startTour: "Start panel tour",
      goHome: "Go to home",
      skipAll: "Skip and open the panel",
      next: "Next",
      back: "Back",
      creatingHouse: "Creating…",
      savingBudget: "Saving…",
      finishing: "Finishing…",
      houseNameLabel: "Home name",
      houseNamePlaceholder: "e.g. Main St, The Smiths…",
      invalidAmount: "Enter a valid amount (a number greater than zero).",
    },
    productTour: {
      sidebarHomeTitle: "Dashboard",
      sidebarHomeDesc: "Return here for the active home overview: balance, month spend, and quick links.",
      navHousesTitle: "Homes",
      navHousesDesc:
        "Create a home, pick the active one, and open members or per-house settings. Without a home there are no linked expenses or budget.",
      navJournalTitle: "Journal",
      navJournalDesc:
        "Expense list for the active home: one-off and recurring, filters and detail. If you have no home yet, this link takes you to Homes.",
      navBudgetTitle: "Budget",
      navBudgetDesc:
        "Monthly cap, category plan, and tracking against real spend. Set it up once your home is ready.",
      homeMembersTitle: "Household members",
      homeMembersDesc:
        "Open invitations, roles, and default split. You can also reach it from Homes → your home → Members.",
      sidebarAddExpenseTitle: "New expense",
      sidebarAddExpenseDesc: "Log a one-off expense or open the form to add it to the journal.",
      sidebarAddExpenseClosingDesc:
        "Use this anytime to log spending. That ends the tour—you now know how to create a home, invite people, choose whether a new expense is shared (household) or personal, pick who paid, and plan the budget.",
      housesCreateTourTitle: "Create your home",
      housesCreateTourDesc:
        "Enter a name and create: it holds expenses, members, and budget. You can have several and pick the active one for day-to-day use.",
      housesEmptyTourTitle: "No homes yet",
      housesEmptyTourDesc:
        "After you create the first one you will see the summary here and can open Members and Budget. Run this tour again from Settings whenever you like.",
      housesMembersLinkTourTitle: "Household members",
      housesMembersLinkTourDesc:
        "From Members you invite by email, set roles, and the default split for shared expenses. Do this before sharing lots of spending.",
      membersInviteTourTitle: "Invite to the home",
      membersInviteTourDesc:
        "Enter the email and role (member or admin). They get a join link; pending invites appear below.",
      membersSplitTourTitle: "Split between members",
      membersSplitTourDesc:
        "Percentages total 100% and set each person’s share on shared expenses. Only admins and owners can save changes.",
      budgetHeroTourTitle: "Budget by month",
      budgetHeroTourDesc:
        "Pick the month with the arrows or the month picker. You will see the planned total and comparison with the previous month when data exists.",
      budgetGoalTourTitle: "Goal and progress bar",
      budgetGoalTourDesc:
        "The bar compares what is already paid this month with the cap or the sum planned by category. If there is a monthly cap, it is shown as context.",
      homeQuickActionsTitle: "Quick actions",
      homeQuickActionsDesc: "Shortcuts to add an expense, scan a receipt, or manage members.",
      settingsReplay: "Show the panel tour again",
      settingsReplayHint: "Opens home with the guided interface walkthrough.",
      fallbackTitle: "Your dashboard",
      fallbackBody:
        "Use the side menu (or mobile): Homes to register the household, Journal and Budget to work, and from home the quick links to members and expenses.",
    },
    auth: {
      appName: "HomeExpense",
      loginTitle: "Welcome back",
      loginSubtitle: "Enter your credentials to access your financial journal.",
      registerTitle: "Create your space",
      registerSubtitle: "Sign up to manage household expenses with editorial calm.",
      forgotTitle: "Recover access",
      forgotSubtitle: "Enter your email and we will send you a link to reset your password.",
      resetTitle: "New password",
      resetSubtitle: "Choose a strong password for your account.",
      name: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "name@example.com",
      passwordLabel: "Password",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirm password",
      signInCta: "Sign in",
      signUpCta: "Create account",
      sendResetLink: "Send link",
      saveNewPassword: "Save password",
      createAccountLink: "Create your space",
      registerPrompt: "Don't have an account?",
      alreadyHaveAccount: "Already have an account?",
      backToLogin: "Back to sign in",
      forgotLink: "Forgot password?",
      forgotSentHint: "If an account exists for that email, you will receive a link shortly.",
      resetSuccess: "Password updated. You can sign in now.",
      invalidResetToken: "This link is invalid or has expired. Request a new one.",
      signingIn: "Signing in…",
      signingUp: "Creating account…",
      inviteLoginBanner: "Sign in with the invited email to join the household.",
      dividerOr: "or",
      continueWithGoogle: "Continue with Google",
      continueWithApple: "Continue with Apple",
      continueWithPasskey: "Continue with Passkey",
      signUpWithPasskey: "Sign up with Passkey",
      registerWithPasskeyAfterSignUp: "Register a passkey after creating the account",
      continuingWithGoogle: "Connecting with Google…",
      continuingWithApple: "Connecting with Apple…",
      continuingWithPasskey: "Connecting with Passkey…",
      passkeyPreferredHint:
        "If you already registered a passkey, it is listed first; your browser may suggest it automatically when you tap continue.",
      errors: {
        generic: "Something went wrong. Please try again.",
        invalidEmailOrPassword: "Invalid email or password.",
        userAlreadyExists: "An account with this email already exists.",
        passwordTooShort: "Password is too short.",
        passwordTooLong: "Password is too long.",
        invalidEmail: "Email is not valid.",
        invalidToken: "This link is invalid or has expired.",
        resetDisabled: "Password recovery is not available.",
        invalidOrigin: "Origin not allowed. Check CORS and VITE_APP_URL.",
        network: "Network error. Check your connection and API URL.",
        emailNotVerified: "Please verify your email before signing in.",
        invalidPassword: "The current password is incorrect.",
        credentialAccountNotFound: "This account has no password (e.g. social sign-in only). Use forgot password or contact support.",
        providerNotConfigured: "Google sign-in is not available. Check the server configuration.",
      },
    },
    houses: {
      pageTitle: "My houses",
      createTitle: "New house",
      nameLabel: "Name",
      namePlaceholder: "e.g. Main home",
      createCta: "Create house",
      creating: "Creating…",
      yourRole: "Your role",
      membersLink: "Members",
      setActive: "Use this house",
      emptyTitle: "You have no houses yet",
      emptyHint: "Create a house to start tracking expenses with your household.",
      cardSummaryPeriod: "Summary {{yearMonth}}",
      cardPlannedShort: "Planned",
      cardSpentShort: "Paid",
      cardUsageShort: "Usage",
      cardStatusWithin: "Within budget",
      cardStatusTight: "Near the limit",
      cardStatusOver: "Over budget",
      cardStatusNeutral: "No monthly cap or plan",
      cardSummaryError: "Could not load summary",
      openingBalanceLabel: "Opening bank balance",
      openingBalanceEdit: "Edit opening balance",
      openingBalanceSave: "Save",
      openingBalanceCancel: "Cancel",
      openingBalanceSaving: "Saving…",
      openingBalanceAmountLabel: "Amount",
      openingBalanceHint: "Reference bank balance when setting up the house (minor units: cents). Shown on the dashboard and header.",
      openingBalanceReadOnlyHint: "Only owners or admins can change the opening balance.",
      openingBalanceInvalidAmount: "Enter a valid amount (0 or greater).",
      openingBalanceLoadError: "Could not load opening balance.",
      financialProfileSectionTitle: "Financial profile",
      financialProfileSubtitle: "Reference opening balance, household currency, and optional monthly cap.",
      financialProfileEdit: "Edit financial profile",
      financialProfileFinancingsLink: "View financings",
      currencyLabel: "Currency (ISO 4217)",
      currencyPlaceholder: "Three-letter code, e.g. EUR",
      monthlyCapLabel: "Monthly cap (optional)",
      monthlyCapPlaceholder: "e.g. 2500.50",
      monthlyCapHint: "Household reference limit; leave empty to remove the cap.",
      monthlyCapNone: "No monthly cap",
      financialProfileReadOnlyHint: "Only owners or admins can edit the financial profile.",
      financialProfileInvalidCurrency: "Currency must be a 3-letter ISO code (e.g. EUR).",
      monthlyCapInvalidAmount: "Enter a valid cap amount or leave the field empty.",
      financialProfileSaveSuccess: "Financial profile saved.",
      financialProfileLoadError: "Could not load the financial profile.",
      setActiveSuccess: "Active house updated.",
      editNameCta: "Edit name",
      editNameSave: "Save name",
      editNameCancel: "Cancel",
      deleteCta: "Delete house",
      deleteConfirm:
        "Delete this house? Related data will be removed from the system. This cannot be undone.",
      updateNameSuccess: "House name updated.",
      updateNameError: "Could not update the name. Please try again.",
      deleteSuccess: "House deleted.",
      deleteError: "Could not delete the house. Please try again.",
    },
    members: {
      pageTitle: "Members",
      roleLabel: "Role",
      roleAdmin: "Admin",
      roleMember: "Member",
      removeCta: "Remove",
      removeConfirm: "Remove this member from the house?",
      columnUser: "User",
      columnRole: "Role",
      columnSplit: "Split (%)",
      backToHouses: "Back to houses",
      inviteSectionTitle: "Invite by email",
      inviteSectionAdminOnly: "Only owners and admins can invite or manage members.",
      inviteHelp:
        "We will email a link to create an account or sign in. The invite must be accepted with the same email address.",
      inviteEmailLabel: "Guest email",
      inviteEmailPlaceholder: "name@example.com",
      inviteSendCta: "Send invitation",
      inviteSending: "Sending…",
      inviteSent: "Invitation sent. They will receive an email with next steps.",
      pendingInvitesTitle: "Pending invitations",
      pendingInviteExpires: "Expires {{date}}",
      pendingInviteExpiredBadge: "Expired",
      revokeInviteCta: "Revoke",
      revokeInviteConfirm: "Revoke this invitation?",
      registerInviteHeadline: "You are invited",
      registerInviteSubline: "Join “{{houseName}}” — use the email this invitation was sent to.",
      emailLockedForInvite: "This email is set by your invitation.",
      inviteAcceptWrongEmail: "Sign in with the email the invitation was sent to.",
      inviteAcceptedJoined: "You have joined the household.",
      inviteAcceptedAlreadyMember: "You were already a member; the invitation was closed.",
      removeOwnerNotAllowed: "The owner cannot be removed from here.",
      removeMemberSuccess: "Member removed from the house.",
      changeRoleSaved: "Role updated.",
      roleOwner: "Owner",
      splitSectionTitle: "Split configuration",
      splitSectionHelp:
        "Set each member’s share of the monthly budget. Shares always total 100%: the last share is calculated automatically.",
      splitBudgetEyebrow: "Monthly budget",
      splitNoBudgetHint:
        "Without a monthly cap we cannot show amounts per person. Set it in the budget view.",
      splitOpenBudgetLink: "Go to budget",
      splitYouBadge: "You",
      splitTotalLine: "Total: {{percent}} %",
      splitTotalValid: "Splits add up to 100%",
      splitRemainderHint: "Calculated automatically to reach 100%.",
      splitSaveCta: "Save split",
      splitSaving: "Saving…",
      splitSaveSuccess: "Split saved.",
      splitReadOnlyHint: "Only admins and owners can change the split.",
    },
    home: {
      periodLabel: "Period {{yearMonth}}",
      spentMonth: "Spent this month",
      totalToContributeLabel: "Total to contribute this month",
      plannedBudget: "Planned budget",
      budgetUsage: "Budget usage",
      openingBalance: "Reference balance",
      upcomingTitle: "Upcoming expenses",
      upcomingEmpty: "No upcoming due dates.",
      ledgerRecentEmpty:
        "No expenses yet for this house. Add one from the journal or use «New expense» to see it here.",
      quickActions: "Quick actions",
      actionExpense: "Add expense",
      actionScan: "Scan receipt",
      actionMembers: "Manage members",
      insightsStub: "Comparisons and savings tips will appear here soon.",
      heroOpeningLabel: "Opening bank balance",
      heroProjectedBalanceLabel: "Estimated account balance",
      heroProjectedBalanceHint:
        "Base {{opening}} + budget {{need}} − paid {{spent}} − retained {{retained}}",
      heroStatSpentLabel: "Paid by the household in the period",
      heroStatForecastExpensesLabel: "Forecast expenses in period",
      heroStatPlannedLabel: "Planned budget",
      insightBadge: "Smart insight",
      insightTitleWithin: "You are on track",
      insightTitleTight: "Watch the budget",
      insightTitleOver: "Budget exceeded",
      insightTitleNeutral: "Period overview",
      insightBodyWithin:
        "You have used {{percent}}% of the planned budget this month—room to adjust calmly.",
      insightBodyTight: "Much of the budget is already used. Review the journal before new spending.",
      insightBodyOver: "Spending exceeds the planned budget ({{percent}}%). Consider reviewing categories.",
      insightBodyNeutral: "Set a monthly cap in the house financial profile for automatic tracking.",
      insightCta: "See details in journal",
      ledgerEyebrow: "The ledger",
      ledgerTitle: "Recent movements",
      ledgerViewAll: "Open full journal",
      budgetProgressSubline: "{{spent}} of {{cap}} used (household)",
      gridSpentHint: "This month",
      gridForecastHint: "Forecast",
      forecastExpensesMonth: "Committed (all charges)",
      gridBudgetHint: "Plan",
      gridUsageHint: "Usage",
      gridSpentSharedLine: "Household (shared)",
      gridSpentPersonalLine: "Personal (only payer)",
      gridBudgetMonthlyLabel: "Monthly budget",
      ledgerRowBadge: "Upcoming",
      moneySnapshotTitle: "Month money snapshot",
      moneySnapshotOpening: "Opening balance",
      moneySnapshotNeedMonth: "Need this month",
      moneySnapshotRetained: "Retained (prorated)",
      moneySnapshotAvailableAfterRetained: "Available after retained",
      moneySnapshotProjectedBalance: "Estimated account balance",
      budgetSetupUrgentTitle: "No monthly budget reference yet",
      budgetSetupUrgentBody:
        "Without category lines or a household monthly cap, progress and alerts are less meaningful. Set a plan or a cap in Budget.",
      budgetSetupUrgentCta: "Open budget",
      budgetSetupCapOnlyTitle: "You have a monthly cap — add categories",
      budgetSetupCapOnlyBody:
        "The household cap guides spending pace. Distributing amounts by category gives clearer control.",
      budgetSetupCapOnlyCta: "Plan by category",
    },
    expenses: {
      newCta: "New expense",
      paid: "Paid",
      unpaid: "Pending",
      paidBy: "Who paid",
      scopeShared: "Household",
      scopePersonal: "Personal",
      category: "Category",
      backToJournal: "Back to journal",
      backToExpenseDetail: "Back to expense",
      ledgerMonthTotalLabel: "Paid this month (settled charges only)",
      ledgerMonthForecastTotalLabel: "Month forecast (all charges)",
      ledgerMemberSharesTitle: "Share of paid amount (from splits)",
      ledgerMemberSharesForecastTitle: "Forecast share (all charges)",
      ledgerNoSplitsInMonth:
        "No expenses this month have a member split; the total includes all charges.",
      listForecastVsActual: "Forecast: {{amount}}",
      actionMarkPaid: "Mark paid",
      actionSetActualAmount: "Actual amount",
      markPaidConfirmTitle: "Mark as paid?",
      markPaidConfirmBody:
        "The expense will count as settled for this month’s progress. You can set the real amount afterwards if needed.",
      markPaidConfirmCta: "Yes, mark paid",
      actionPaidZero: "Paid at €0",
      paidZeroConfirmTitle: "Settle at zero",
      paidZeroConfirmBody:
        "The real amount will be saved as €0 and the expense will be marked paid. Use when you skip spending this month (e.g. bi‑monthly accrual): no cash out this month; the real payment shows in the month you pay.",
      paidZeroConfirmCta: "Yes, €0 and mark paid",
      actualAmountModalTitle: "Actual expense amount",
      actualAmountModalHint:
        "The forecast stays as planned; the actual helps compare to budget. If you leave it empty, the forecast is used.",
      actualAmountLabel: "Actual amount paid",
      actualAmountClear: "Clear actual amount",
      actualAmountSubmit: "Save",
      paidFeedbackTitle: "Confirmation",
      paidFeedbackHero: "Expense marked as paid!",
      paidFeedbackSubline: "Your tracking is back in sync.",
      paidFeedbackAmountLabel: "Amount recorded",
      paidFeedbackCategoryLabel: "Category",
      paidFeedbackPaidByLabel: "Paid by",
      paidFeedbackDateLabel: "Date",
      paidFeedbackContribution: "{{percent}}% share",
      paidFeedbackBackHome: "Back to home",
      paidFeedbackViewDetail: "View expense details",
      form: {
        titleLabel: "Title",
        titlePlaceholder: "e.g. Groceries",
        titleOptionalHint: "If empty and a category is selected, the category name is used.",
        amountLabel: "Total amount",
        amountHint: "Use comma or dot for decimals (e.g. 24.90).",
        currencyLabel: "Currency (ISO)",
        dateLabel: "Date",
        categoryLabel: "Category",
        categoryNone: "No category",
        paidByLabel: "Who paid",
        paidByDefault: "You (signed-in user)",
        paidByJointOption: "Joint account",
        paidByHint:
          "Defines the source of funds for the expense (personal funds, household joint account, or you by default). It does not decide whether the expense is shared—use “Household expense” for that.",
        paymentMethodLabel: "Payment method",
        paymentMethodNone: "Not specified",
        paymentMethodCard: "Card",
        paymentMethodCash: "Cash",
        paymentMethodAccount: "Bank account",
        sharedLabel: "Household expense (split across members)",
        sharedHint:
          "When enabled, the split sums to 100% across all household members. If disabled, it becomes a personal expense (not split).",
        recurringQuestion: "Is this a recurring expense?",
        recurringEndsLabel: "End date",
        recurringEndsOptionalHelp:
          "Optional when proration is off: leave blank for an open-ended series (utilities, etc.).",
        recurringEndsRequiredWhenProratedHelp:
          "With monthly frequency and proration, an end date is required (split a total across N installments).",
        recurringEndsProrateOpenPeriodHelp:
          "You can leave the date blank: the total is the amount for each billing period (e.g. the bimonthly bill) and monthly charges split it across that period.",
        recurringFrequencyLabel: "Frequency",
        frequencyMonthly: "Monthly",
        frequencyBimonthly: "Every two months",
        frequencyQuarterly: "Quarterly",
        frequencySemiannual: "Semiannual",
        frequencyAnnual: "Annual",
        proratedLabel: "Prorate the total across installments",
        proratedHint:
          "When enabled, the total is divided across payments by frequency. When disabled, each date records the full amount.",
        recurringSummary: "Planned installments: {{count}}. Amount per period: {{installment}}.",
        recurringOpenEndedSummary:
          "No end date: installments are created for about {{months}} months ahead; every month the system checks the series and extends the horizon automatically.",
        recurringProratedOpenPeriodPreviewNote:
          "The total is for each {{periodMonths}}-month billing period; the journal shows one charge per month splitting that total within the period.",
        submitRecurring: "Activate recurring expense",
        notesLabel: "Notes",
        notesPlaceholder: "Optional",
        receiptLabel: "Receipt (optional)",
        receiptHelp: "PDF or image (JPEG, PNG, WebP, GIF). Uploaded securely to storage.",
        receiptRemove: "Remove file",
        paidNowLabel: "Record as paid now",
        submitCreate: "Save expense",
        submitSave: "Save changes",
        saving: "Saving…",
        cancelEdit: "Cancel edit",
        invalidAmount: "Enter a valid amount (0 or greater).",
        titleOrCategoryRequired: "Enter a title or pick a category.",
        recurrenceInvalid: "End date must be after the start date and there must be at least one charge in the range.",
        recurringProratedRequiresEndsOn:
          "When prorating the total, set a series end date (a total cannot be split without an end of the period).",
        recurringPreviewNeedAmount:
          "Enter the total amount above to see the installment calculation (series with end date or proration).",
        recurringPreviewNeedAmountMonthlyFixed:
          "Enter one month’s amount above to see the summary and estimated split.",
        proratedMonthlyAverageHint: "Approximate average over the period: {{amount}} / month",
        recurrenceKindLegend: "Recurring type",
        recurrenceKindMonthlyFixed:
          "Fixed monthly charge (same amount every month; no end date required)",
        recurrenceKindSeries:
          "Series by frequency (monthly, bimonthly…); with or without an end date when proration is off",
        seriesSingleChargeHint:
          "For one charge per period (annual, quarterly, semiannual, or every two months), use Series by frequency with proration disabled.",
        seriesAnnualPresetAction:
          "Use annual subscription preset (no proration)",
        amountLabelMonthly: "Amount per month",
        amountHintMonthlyFixed:
          "Enter what you pay in a single month (e.g. mortgage). It is not a lump sum to split: that same amount repeats each month.",
        monthlyFixedPatternExplain:
          "No end date is required: the journal gets {{count}} charges, one per month, each for the same “one month” amount you enter above (e.g. monthly mortgage). It is not a lump sum split across {{count}} months—every month repeats that amount.",
        monthlyFixedHorizonNote:
          "You’ll see {{count}} journal lines (one per month); each uses the split you set below.",
        smoothBimonthlyBillHint:
          "If a bill arrives every two months (e.g. water or trash) and you want to set aside half each month, use this mode and enter half the bimonthly amount as “one month”.",
        sharedSplitPreviewTitle: "Estimated split (this period)",
        sharedSplitPreviewSubtitle:
          "Amount per person from the household split. If no percentages are set under Members, it is divided equally.",
        sharedSplitUsesHousePercent: "Using percentages from Members",
        sharedSplitEqualParts: "Equal shares between members",
      },
      detail: {
        edit: "Edit",
        markPaid: "Mark as paid",
        markUnpaid: "Mark as pending",
        delete: "Delete",
        deleteConfirm: "Delete this expense? This cannot be undone.",
        paidAtLabel: "Paid on",
        notesTitle: "Notes",
        splitsTitle: "Split",
        splitsSubtitle:
          "Amount for each person based on the percentages stored on this expense.",
        splitShare: "{{share}} %",
        notApplicable: "—",
        scopeLabel: "Scope",
        paymentMethod: "Payment method",
        receiptLabel: "Receipt",
        receiptView: "View receipt",
        receiptNotAttached: "No receipt attached",
        receiptReplaceHelp:
          "You can upload a new receipt to replace the current one without leaving this screen.",
        seriesLink: "View recurring series",
        seriesFutureInstallmentLabel:
          "Amount per installment (updates unpaid future charges)",
        seriesSave: "Save series changes",
        seriesLimitSectionTitle: "Shorten or end",
        seriesLimitSectionIntro:
          "Set the last charge date or end the series now. Do not use this section if you want to remove the end-date limit (see below).",
        seriesEndsOnLabel: "Last charge (end date)",
        seriesEndsOnHelp:
          "Unpaid installments dated after this date will be removed. The charge on this date is kept.",
        seriesApplyEndsOn: "Save end date",
        seriesEndNow: "End series now",
        seriesEndNowConfirm:
          "Future unpaid installments will be removed and the series will stop generating charges. Continue?",
        seriesExtendSectionTitle: "Extend the series",
        seriesExtendIntroActiveWithEnd:
          "Remove the end date so installments are generated again up to the system horizon. On prorated series, installments will use the fixed per-installment amount until you set an end date again.",
        seriesExtendIntroEnded:
          "Reactivate the series and remove the end date. Future unpaid installments will be generated again. Do not use this if you only wanted to shorten the series (use the section above before ending).",
        seriesRemoveEndsOn: "Remove end date",
        seriesRemoveEndsOnConfirm:
          "Remove the end date? The series stays active and installments will be generated for the usual horizon.",
        seriesReactivateOpenEnded: "Reactivate with no end date",
        seriesReactivateOpenEndedConfirm:
          "Reactivate the series and remove the end date? Future unpaid installments will be created again according to the recurrence.",
        seriesReactivateOnly: "Reactivate series",
        seriesReactivateOnlyConfirm:
          "Reactivate the series? It will be active again with the current settings.",
        seriesExtendIntroEndedNoEnd:
          "This series is ended and has no end date on record. Reactivate it to generate installments again within the usual horizon.",
        seriesStatusBadgeActive: "Active",
        seriesStatusBadgePaused: "Paused",
        seriesStatusBadgeEnded: "Ended",
        seriesEndedReadOnly:
          "This series has ended. To generate charges again, use “Extend the series”.",
        seriesInvalidEndDate: "Enter a valid date (YYYY-MM-DD).",
        budgetSetupBannerTitle: "No budget reference for this month",
        budgetSetupBannerBody:
          "This expense’s month has no category budget and no household monthly cap. Set them in Budget for clearer tracking.",
        budgetSetupBannerCta: "Open budget",
      },
      filters: {
        title: "Filters",
        searchPlaceholder: "Search the journal…",
        scopeLabel: "Scope",
        scopeAll: "All",
        scopeShared: "Household",
        scopePersonal: "Personal",
        categoryAll: "All categories",
        paidAll: "All",
        paidYes: "Paid only",
        paidNo: "Unpaid only",
        apply: "Apply",
        clear: "Clear",
      },
      flows: {
        launcherTitle: "What do you want to do?",
        launcherSubtitle:
          "Choose a path based on the type of expense you want to log.",
        manualOnlyBanner:
          "Enter the key expense details: amount, date, and payment method.",
        intentQuickTitle: "Log a one-off expense",
        intentQuickDescription:
          "For a charge you already made: amount, category, and how you paid. Everything else only if you need it.",
        intentRecurringTitle: "Fixed monthly expense",
        intentRecurringDescription:
          "A charge that repeats (rent, HOA…). A short guided flow explains what you are setting up.",
        intentSeriesTitle: "Series with dates or proration",
        intentSeriesDescription:
          "Installments with an end date, different frequencies, or splitting a total across months. Review before saving.",
        quickExpandMore: "More options (title, notes, shared…)",
        repeatLastFlowCta: "Continue with: {{flowLabel}}",
        navNext: "Next",
        navBack: "Back",
        navReview: "Review",
        editStepA11y: "Edit step {{step}}",
        monthlyWizard: {
          step1Title: "What is this expense?",
          step1Help: "Title or category so you can recognize it in the journal every month.",
          step2Title: "How much do you pay per month?",
          step2Help: "A single monthly amount. We repeat it for each installment.",
          step3Title: "When does it start?",
          step3Help: "First charge date or the day you want it to appear in the journal.",
          step4Title: "How do you pay it?",
          step4Help: "Card, cash, or account—you record how the money leaves (all manual).",
          step5Title: "Is it shared at home?",
          step5Help: "Who pays and whether the expense is shared across members.",
          reviewTitle: "Your fixed expense will look like this",
          reviewHelp: "Check the details. You can go back and change any step.",
        },
        seriesWizard: {
          step1Title: "Title and amount",
          step1Help:
            "What this is and the amount for the mode you choose (per month or period total—set in the next steps).",
          step2Title: "How often it repeats",
          step2Help: "Monthly, quarterly, yearly… Frequency shapes how charges show in the journal.",
          step3Title: "Start and end",
          step3Help:
            "Start date and, if needed, last installment. With no end date, the series may stay open depending on rules.",
          step4Title: "Proration",
          step4Help:
            "If you split a total across installments, this explains the split. No bank link—you confirm amounts.",
          step5Title: "Split across people",
          step5Help: "Shared flag and who paid. You will see a preview when amount and dates are ready.",
          reviewTitle: "Series summary",
          reviewHelp: "Confirm installments and dates before creating the series.",
        },
      },
    },
    financings: {
      pageTitle: "Financing",
      newCta: "New financing",
      emptyTitle: "No financing yet",
      emptyHint:
        "Record loans or fixed installments with a contract start date. Past installments are not added to the journal.",
      stepDataTitle: "Details",
      stepReviewTitle: "Review",
      titleLabel: "Name",
      lenderLabel: "Lender or institution",
      installmentLabel: "Monthly installment",
      contractStartLabel: "Contract start (installment schedule)",
      endsOnLabel: "Last installment date",
      notesLabel: "Notes",
      docsHint: "Optional documents (PDF or image). Upload here or continue with manual data only.",
      runAiLabel: "Analyze documents with AI",
      runAiHint:
        "Summarises key terms for information only. Not legal or financial advice.",
      generateRecurringLabel: "Generate monthly recurring expense",
      generateRecurringHint:
        "Creates a journal series from the first installment on or after today (UTC) through the end date.",
      reviewCutoff: "Cutoff date: {{date}} (UTC)",
      reviewFirstPayment: "First journal installment: {{date}}",
      reviewFutureCount: "Future installments: {{count}}",
      reviewPreviewDates: "Upcoming dates (sample)",
      reviewWillSeries: "A recurring expense series will be created",
      reviewWillAi: "The first document will be sent to AI after save",
      reviewNoSeries: "No automatic expenses will be created",
      backCta: "Back",
      continueCta: "Continue to review",
      confirmCta: "Confirm and save",
      saving: "Saving…",
      aiDisclaimer: "AI summaries are indicative and may contain errors.",
      aiSkipped: "AI not used",
      aiPending: "AI summary pending",
      aiProcessing: "Analyzing documents…",
      aiCompleted: "AI summary ready",
      aiFailed: "AI summary failed",
      viewSeriesCta: "View expense series",
      listInstallment: "Installment: {{amount}}",
      listTermProgress: "{{percent}}% of term",
      listContractEndedBadge: "Ended",
      listInstallmentsSummary:
        "{{past}} of {{total}} installments elapsed · {{remaining}} remaining",
      listEstimatedRemaining: "Estimated outstanding: {{amount}}",
      listNoInstallmentSchedule: "No installment schedule",
      categoryLabel: "Category",
      categoryNone: "No category",
      openDocument: "Open document",
      summaryTitle: "AI summary",
      keyTermsTitle: "Key terms",
      editCta: "Edit",
      deleteCta: "Delete",
      deleteConfirm:
        "Delete this financing? If it had a linked recurring expense series, the series will be marked as ended.",
      saveCta: "Save changes",
      saveSuccess: "Financing updated.",
    },
    budget: {
      heroEyebrow: "Household allocation",
      heroTitle: "Planned household spend this month",
      heroSubtitle:
        "Total of your household budget lines for this period. Personal expenses are tracked separately in the journal.",
      goalLabel: "Goal progress: {{yearMonth}}",
      percentComplete: "{{percent}}% complete",
      reservedLabel: "{{amount}} household spent",
      targetLabel: "{{amount}} target",
      categoryGridTitle: "By category",
      plannedLabel: "Planned",
      spentLabel: "Household spent",
      plannedInputHint: "Planned amount (major units)",
      monthNav: "Month",
      prevMonth: "Previous month",
      nextMonth: "Next month",
      saveCta: "Save budget",
      saving: "Saving…",
      saveSuccess: "Budget updated.",
      copyFromPreviousConfirmTitle: "Copy from previous month?",
      copyFromPreviousConfirmMessage:
        "Budget lines from the previous month will be applied to this month. You can edit them afterwards.",
      copyFromPreviousCta: "Import from previous month",
      readOnlyHint: "Only owners and admins can edit the budget.",
      performanceTitle: "Period performance",
      performanceSubtitle: "Planned household budget vs actual household spending this month.",
      legendBudgeted: "Household planned",
      legendSpent: "Household spent",
      noCategoriesHint:
        "No categories yet. Create the first one with the «Add category» card; it will appear when you record expenses.",
      addCategoryCard: "Add category",
      addCategoryCardHint: "New categories can be selected when creating an expense.",
      addCategoryFormTitle: "New category",
      addCategoryNameLabel: "Name",
      addCategoryNamePlaceholder: "e.g. Groceries",
      addCategorySubmit: "Create",
      addCategoryCancel: "Cancel",
      addCategoryCreating: "Creating…",
      heroVsPrevUp: "+{{pct}}% vs last month",
      heroVsPrevDown: "−{{pct}}% vs last month",
      heroVsPrevFlat: "Same as last month",
      proratedTitle: "Prorated obligations",
      proratedLinkJournal: "View journal",
      proratedReserveHint: "Estimated monthly installment",
      proratedTotalBadge: "Total {{amount}}",
      proratedEmpty:
        "Series with a prorated total (property tax, annual insurance, etc.) will show here. Create them from the journal as prorated recurring expenses.",
      planningSectionTitle: "Category planning",
      bannerEyebrow: "Sustainable habits",
      bannerTitle: "A clear budget reduces surprises and aligns your household with what matters.",
      badgeOverBudget: "Over plan",
      badgeUnderBudget: "Under plan",
      badgeOnTrack: "On track",
      badgeNoPlan: "No plan",
      columnBudget: "Budget",
      columnReal: "Actual",
      analysisEmpty:
        "No categories with a plan or spending this month yet. Add amounts below or record expenses in the journal.",
      monthlyCapHint: "Household monthly cap",
      moneySnapshotTitle: "Quick cash snapshot",
      moneySnapshotOpening: "Opening balance",
      moneySnapshotNeedMonth: "Need this month",
      moneySnapshotRetained: "Retained (prorated)",
      moneySnapshotAvailableAfterRetained: "Available after retained",
      moneySnapshotProjectedBalance: "Estimated account balance",
    },
    vault: {
      pageTitle: "Vault",
      pageSubtitle:
        "Securely manage home warranties and policies. We will remind you before coverage expires.",
      emptyHint: "Record warranties, policies, and assets with key dates.",
      addCta: "Add asset",
      nameLabel: "Item name",
      namePlaceholder: "e.g. Samsung Smart Fridge",
      assetTypeLabel: "Category",
      assetTypePlaceholder: "e.g. Policy, appliance",
      purchaseDateLabel: "Purchase date",
      warrantyLabel: "Warranty ends",
      notesLabel: "Notes",
      notesPlaceholder: "Contract details, references…",
      notesOptionalHint: "Optional",
      documentKeyLabel: "Document key (storage)",
      documentKeyPlaceholder: "After uploading a file to storage",
      saveCta: "Save",
      saving: "Saving…",
      createCta: "Save to vault",
      backToList: "Back to vault",
      deleteCta: "Delete",
      deleteConfirm: "Remove this vault item?",
      readOnlyHint: "Only owners and admins can create or edit items.",
      cardWarranty: "Warranty until {{date}}",
      cardPurchased: "Purchased {{date}}",
      formKicker: "Registration",
      formTitle: "New vault asset",
      formSubtitle: "Protect your purchase with original details and proof of purchase.",
      priceLabel: "Price / value",
      pricePlaceholder: "0.00",
      priceInNotesLine: "Estimated value: {{amount}}",
      documentUploadTitle: "Receipt / invoice",
      documentUploadHint: "Upload proof of purchase",
      documentUploadSub: "Tap to choose a file or image",
      documentUploadedHint: "Document attached",
      completionLabel: "Registration progress",
      saveLegalNote:
        "By saving, you authorize secure cloud storage of your purchase documents and warranty dates.",
      filterAll: "All",
      filterEmpty: "No items in this category.",
      alertKicker: "Alert",
      alertTitle: "Warranty attention",
      alertDaysLeft: "“{{name}}” warranty expires in {{days}} days.",
      alertExpired: "The warranty for “{{name}}” has expired. Review coverage.",
      alertViewDetails: "View details",
      statTotalLabel: "Vault assets",
      statSecuredLabel: "With warranty date",
      statSecuredLine: "Items with a current or future warranty end date",
      sectionCount: "{{count}} items",
      cardPurchaseDate: "Purchase date",
      cardExpiryDate: "Warranty ends",
      viewDocument: "View receipt",
      registerNewItem: "Register new item",
      warrantyBadgeExpired: "Expired",
      warrantyBadgeUrgent: "Urgent",
      warrantyBadgeWarning: "Soon",
      warrantyBadgeSafe: "OK",
      warrantyBadgeNone: "No date",
      warrantyEndsToday: "Ends today",
      categoryPlaceholder: "Choose category",
      sectionUncategorized: "Uncategorized",
      categories: {
        appliances: "Appliances",
        electronics: "Electronics",
        furniture: "Furniture",
        vehicles: "Vehicles",
        other: "Other",
      },
    },
    scan: {
      overlayTitle: "AI scan",
      overlaySubtitle: "Upload a receipt photo; processing may take a moment.",
      pickFile: "Choose image",
      uploading: "Uploading…",
      registering: "Registering scan…",
      statusPending: "Queued",
      statusProcessing: "Processing",
      statusCompleted: "Completed",
      statusFailed: "Failed",
      extractedTitle: "Detected data",
      rawPayloadHint: "Raw result preview (JSON).",
      recentTitle: "Recent scans",
      linkExpenseHint: "Review the extracted data and create the expense in the journal when the scan is complete.",
      pollHint: "Refreshing status…",
      createExpenseFromScan: "Create expense with this data",
      fromScanBanner: "Fields prefilled from an AI scan. Review before saving.",
      extractionErrorFallback: "Could not interpret the receipt.",
      extractedMerchant: "Merchant",
      extractedDate: "Date",
      extractedTotal: "Total",
    },
    profile: {
      pageTitle: "Profile",
      accountSettings: "Account & data",
      accountSettingsHint: "Personal preferences",
      notifications: "Notifications",
      notificationsHint: "Push, preferences, and devices",
      devices: "Devices and sessions",
      devicesHint: "See where you are signed in and sign out remotely",
      privacy: "Privacy & security",
      privacyHint: "Password and permissions",
      help: "Help & support",
      helpHint: "FAQs and contact",
      logout: "Log out",
      logoutHint: "Sign out securely",
      footerNote: "HomeExpense",
      profileFormSectionTitle: "Profile and language",
      nameField: "Display name",
      saveButton: "Save changes",
      saving: "Saving…",
      savedHint: "Changes saved.",
      clearAvatarButton: "Remove image",
      avatarChangeAriaLabel: "Change profile photo",
      avatarUploading: "Uploading image…",
      avatarUploadError: "Could not upload the image. Try JPG, PNG, or WebP (max 5 MB).",
    },
    personalDataPage: {
      subtitle: "Email, interface language, and display name.",
      emailLabel: "Email",
      emailHint: "Email linked to your account. It cannot be edited here.",
      deleteAccountSectionTitle: "Delete account",
      deleteAccountSectionHint:
        "Permanently deletes your user, sessions, and personal data. In shared households, another member will become the creator on records that remain.",
      deleteAccountButton: "Delete my account and data",
      deleteAccountModalTitle: "Delete account permanently?",
      deleteAccountModalBody:
        "This cannot be undone. You will lose access to HomeExpense and your account data will be removed. If you shared a household, other members keep the household and history is reassigned.",
      deleteAccountModalConfirm: "Yes, delete my account",
      deleteAccountModalCancel: "Cancel",
      deleteAccountError: "Deletion could not be completed. Try again or contact support.",
      deleteAccountPending: "Deleting account…",
    },
    settingsHub: {
      unifiedPageTitle: "Account & data",
      unifiedSubtitle: "Profile photo and links to personal details, security, and help.",
      backToAccount: "Back to account & data",
      cardPersonalDataTitle: "Personal details",
      cardPersonalDataHint: "Language, display name, and email",
      cardPasswordTitle: "Password",
      cardPasswordHint: "Change password while signed in",
      cardPasskeysTitle: "Passkeys",
      cardPasskeysHint: "Add or manage passwordless sign-in on this browser",
      cardNotificationsTitle: "Notifications",
      cardNotificationsHint: "Device push and alert types",
      cardDevicesTitle: "Devices and sessions",
      cardDevicesHint: "Active sessions and remote sign-out",
      cardSupportTitle: "Help & support",
      cardSupportHint: "Recover access and FAQs",
      cardDeleteAccountTitle: "Delete account",
      cardDeleteAccountHint: "Permanently remove your user and associated data",
    },
    settingsPasswordForm: {
      subtitle: "Enter your current password and a new one. If you forgot the current password, use forgot password instead.",
      currentPassword: "Current password",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
      submit: "Update password",
      successMessage: "Password updated successfully.",
      forgotPasswordCta: "I forgot my password",
      noCredentialHint: "No password is set for this account. Use forgot password or your social sign-in provider.",
      changeAgain: "Change again",
    },
    settingsPasskeysPage: {
      subtitle: "Register a passkey on this device for faster sign-in next time.",
      disabledHint: "Passkeys are disabled in this build (VITE_PASSKEY_AUTH_ENABLED).",
      addHint: "Use a platform authenticator (e.g. Touch ID, Windows Hello, or system passkey).",
      addButton: "Add passkey",
      adding: "Registering…",
      loading: "Loading passkeys…",
      loadError: "Could not load passkeys. Please try again.",
      empty: "You have no passkeys registered for this account yet.",
      unnamedPasskey: "Unnamed passkey",
      deviceTypeLabel: "Type: {{type}}",
      createdLabel: "Added:",
      renameButton: "Rename",
      renameDialogTitle: "Passkey name",
      renameLabel: "Display name",
      renameSave: "Save",
      renameEmpty: "Enter a name for the passkey.",
      deleteButton: "Remove",
      deleteConfirm: "Remove this passkey? You will not be able to use it to sign in.",
      deleting: "Removing…",
    },
    supportPage: {
      subtitle: "Useful resources and links.",
      intro: "Help for account access and security-related links.",
      forgotTitle: "Forgot your password?",
      forgotBody: "We email you a link to reset it without knowing the old password.",
      forgotCta: "Go to recover access",
    },
    notificationsPage: {
      subtitle: "Alerts and updates for your home.",
      emptyTitle: "No notifications",
      emptyHint: "When there are updates (invites, reminders, etc.), they will appear here.",
      inboxTitle: "Inbox",
      pushSectionTitle: "Push notifications",
      activatePush: "Enable on this browser",
      pushWorking: "Push is active on this device.",
      permissionDenied: "Notification permission denied. Enable it in your browser settings.",
      notSupported: "This browser does not support web push.",
      configMissing: "Firebase is not configured (missing VITE_FIREBASE_* env vars).",
      registerError: "Could not register this device for push.",
      devicesSectionTitle: "Registered devices",
      noDevices: "No devices yet. Enable push above to register this browser.",
      platformLabel: "Platform",
      platformWeb: "Web",
      platformIos: "iOS",
      platformAndroid: "Android",
      lastSeenLabel: "Last activity",
      removeDevice: "Remove device",
      prefsSectionTitle: "Notification preferences",
      prefPushMaster: "Receive push notifications",
      prefHouseInvites: "Home and invitations",
      prefExpenses: "Expenses and journal",
      prefBudget: "Budget and limits",
      prefReminders: "Reminders",
      prefScanDocs: "Scans and documents",
      prefDigests: "Digests",
      prefUpcomingCharges: "Charges due tomorrow (unpaid)",
      markAllRead: "Mark all as read",
      markOneRead: "Mark as read",
      loadMore: "Load more",
      unreadLabel: "Unread",
      viewAction: "Open",
      removeDeviceConfirm: "Remove this device from push notifications?",
      devicePushLabel: "Push on this device",
    },
    pendingInvitationsPage: {
      title: "Pending invitations",
      subtitle:
        "Accept invitations to join homes here. Until you accept, you cannot access the home or member management.",
      emptyTitle: "No pending invitations",
      emptyHint:
        "If you were invited by email, check the link in the message. In-app notifications will bring you here when there is an active invitation.",
      inviterLine: "Invited by: {{inviterName}}",
      expiresLabel: "Expires on {{date}}",
      acceptCta: "Accept and join",
      accepting: "Joining…",
      loadError: "Could not load invitations. Please try again.",
    },
    notificationSettingsPage: {
      subtitle:
        "Enable browser notifications, choose which alerts you receive, and manage registered devices.",
    },
    notificationsInApp: {
      pushDeviceRegisteredTitle: "Notification device",
      pushDeviceRegisteredBody: "This device ({{platform}}) was registered successfully.",
      receiptScanCompletedTitle: "Receipt ready to review",
      receiptScanCompletedBody:
        "The scan for {{houseName}} finished. You can create an expense from the detected data.",
      receiptScanFailedTitle: "Could not read the receipt",
      receiptScanFailedBody:
        "The scan for {{houseName}} failed. Try another photo or check status in AI scan.",
      houseInvitationReceivedTitle: "Home invitation",
      houseInvitationReceivedBody:
        "{{inviterName}} invited you to join «{{houseName}}». Open the link to accept the invitation.",
      houseInvitationAcceptedTitle: "Someone joined the home",
      houseInvitationAcceptedBody:
        "{{inviteeName}} accepted the invitation and is now part of «{{houseName}}».",
      budgetThreshold80Title: "Budget at {{percent}}%",
      budgetThreshold80Body:
        "In «{{houseName}}», paid spending for {{yearMonth}} has reached {{percent}}% of the target.",
      budgetThreshold100Title: "Budget at the limit",
      budgetThreshold100Body:
        "In «{{houseName}}», paid spending for {{yearMonth}} has reached {{percent}}% of the target.",
      budgetMonthStartedTitle: "New month in {{houseName}}",
      budgetMonthStartedBody: "{{yearMonth}} has started. Review the home budget and goals.",
      budgetMonthNeedsSetupTitle: "Budget pending in {{houseName}}",
      budgetMonthNeedsSetupBody:
        "{{yearMonth}} has started and there was no previous plan to copy. Define category lines in Budget or set a household monthly cap as a fallback.",
      budgetMonthSeededFromPreviousTitle: "Budget prepared in {{houseName}}",
      budgetMonthSeededFromPreviousBody:
        "{{yearMonth}}: your category budget is already saved for this home ({{copiedCount}} lines from {{sourceYearMonth}}). Open Budget only to review or tweak amounts if needed.",
      vaultWarrantyExpiring7dTitle: "Warranty ending soon",
      vaultWarrantyExpiring7dBody:
        "«{{itemName}}» in «{{houseName}}» has warranty until {{endDate}} (about 7 days left).",
      upcomingExpenseTomorrowTitle: "Charges due tomorrow in {{houseName}}",
      upcomingExpenseTomorrowBody:
        "You have {{count}} unpaid expense(s) dated {{tomorrowDate}}. Review the journal so nothing catches you off guard.",
    },
    devicesPage: {
      subtitle: "Control where you are signed in.",
      intro:
        "Each row is an active session. You can sign out on a specific device or on all other devices at once.",
      thisDevice: "This device",
      currentBadge: "Current",
      lastActive: "Last activity",
      sessionStarted: "Signed in",
      expires: "Expires",
      ip: "IP",
      browser: "Browser or app",
      unknownBrowser: "Unknown",
      signOutDevice: "Sign out on this device",
      signOutDeviceConfirm:
        "Sign out on this device? If this is the one you are using now, you will return to the sign-in screen.",
      signOutOthers: "Sign out everywhere else",
      signOutOthersConfirm:
        "Sign out on all other devices? This session will stay active.",
      loadError: "Could not load sessions.",
      actionError: "The action could not be completed. Please try again.",
      empty: "No active sessions.",
    },
  },
  mobile: {
    common: {
      cancel: "Cancel",
      confirm: "Confirm",
      skip: "Skip",
      ok: "OK",
      changesSaved: "Changes saved.",
      retry: "Retry",
      loadError: "Could not load data.",
      signingOut: "Signing out…",
      signOutConfirmTitle: "Sign out",
      signOutConfirmMessage: "Are you sure you want to sign out on this device?",
      signOutFailedTitle: "Could not sign out",
      signOutFailed:
        "Check your connection and try again. If it keeps happening, close the app and open it again.",
    },
    session: {
      checking: "Checking session…",
    },
    api: {
      errors: {
        network: "No connection or the server is not responding. Check your network and try again.",
        generic: "The operation could not be completed. Please try again.",
        serverUnavailable: "The service is temporarily unavailable. Please try again later.",
        missingConfig: "The API URL is not configured. Check your setup or environment.",
      },
    },
    tabs: {
      home: "Home",
      houses: "Houses",
      scan: "Scan",
      budget: "Budget",
      vault: "Vault",
    },
    shell: {
      comingSoon:
        "This section will be available soon. In the meantime you can use the dashboard on the web.",
    },
    home: {
      title: "HomeExpense",
      subtitle: "Your household finance space, with Estate Ledger clarity.",
      heroEyebrow: "OPENING BANK BALANCE",
      heroTitle: "Welcome",
      heroSubtitle: "Manage expenses and budget with calm, editorial focus.",
      heroTrend: "Synced with the API",
      heroRemainingLabel: "{{amount}} estimated remaining",
      openSheet: "Open bottom sheet",
      openPaginatedDemo: "Notifications (API)",
      healthChecking: "API: checking…",
      healthOk: "API: online",
      healthFail: "API: unreachable",
      insightTitle: "Within range",
      insightBody:
        "Your spending pace this month is steady. Open the journal for details.",
      insightBadge: "Efficiency",
      insightValue: "Excellent",
      insightValueGood: "Excellent",
      insightValueFair: "On track",
      insightValueOver: "Exceeded",
      upcomingCardBadge: "Next expense",
      upcomingCardBodyGeneric: "Charge expected in this period.",
      upcomingCardNone: "No pending expenses",
      budgetCardTitle: "Monthly budget",
      budgetCardCompleted: "{{percent}}% used",
      budgetCardTotal: "Total: {{amount}}",
      monthSummaryTitle: "This month's summary",
      monthSummaryPersonalLabel: "Personal",
      monthSummarySharedLabel: "Shared",
      monthSummaryTotalLabel: "Total to contribute",
      monthSummaryForecastPrefix: "Forecast: {{amount}}",
      houseSectionTitle: "Household budget",
      personalSectionTitle: "Personal spending",
      quickActionsTitle: "Quick actions",
      quickExpense: "Log expense",
      quickScan: "Scan receipt",
      quickMembers: "Members",
      quickFinancings: "Financings",
      quickFabA11y: "Open quick actions menu",
      quickFabCloseA11y: "Close quick actions menu",
      quickFabLabel: "Actions",
      quickFabCloseLabel: "Close",
      openJournal: "Open journal",
      openJournalHeaderA11y: "Open journal",
      financingsShortcutTitle: "Financings",
      financingsShortcutSubtitle: "Loans and instalments for your active home.",
      analyticsShortcutTitle: "Household insights",
      analyticsShortcutSubtitle: "Charts for paid vs planned and category signals.",
      openingBalanceLabel: "Month opening balance",
      openingBalanceMock: "€2,450.00",
      defaultHouseName: "Main home",
      notificationsA11y: "Notifications",
      profileA11y: "Profile",
      houseSelectorA11y: "Select active home",
      noHouseTitle: "No homes yet",
      noHouseBody:
        "Create or join a home on the web to sync expenses, budget, and vault data here.",
      chooseHouseTitle: "Active home",
      recentTitle: "Upcoming expenses",
      upcomingViewAll: "View all",
      goToChangePassword: "Change password",
      budgetSetupUrgentTitle: "No monthly budget reference yet",
      budgetSetupUrgentBody:
        "Without category lines or a household monthly cap, progress is harder to read. Set a plan or a cap in Budget.",
      budgetSetupUrgentCta: "Open budget",
      budgetSetupCapOnlyTitle: "Monthly cap set — add categories",
      budgetSetupCapOnlyBody:
        "The cap guides spending pace. Distributing by category gives clearer control.",
      budgetSetupCapOnlyCta: "Plan by category",
    },
    journal: {
      title: "Financial journal",
      searchPlaceholder: "Search the journal…",
      fabA11y: "Add expense",
      fabLabel: "Add expense",
      filterAll: "All",
      filterHousehold: "Household",
      filterPersonal: "Personal",
      filterGroceries: "Groceries",
      filterUtilities: "Utilities",
      filterInsurance: "Insurance",
      groupMonth: "October 2025",
      expense1Title: "Weekly shop",
      expense2Title: "Electricity",
      expense1Meta: "12 Oct · ",
      expense2Meta: "8 Oct · ",
      expense1Amount: "€142.50",
      expense2Amount: "€89.20",
      memberYou: "You",
      badgePaid: "Paid",
      badgeUnpaid: "Pending",
      empty: "No expenses match your search or filter.",
      emptyTitle: "No expenses yet",
      emptyDescription:
        "Log purchases and receipts to see your history by month and stay on top of household spending.",
      emptyFilteredTitle: "No results",
      emptyCta: "Add expense",
    },
    expenseQuickActions: {
      menuA11y: "More actions for this expense",
      markPaidChip: "Mark paid",
      adjustActualChip: "Actual amount",
      paidZeroChip: "Paid at €0",
      paidZeroConfirmTitle: "Settle at zero",
      paidZeroConfirmMessage:
        "The real amount will be saved as 0 and the expense will be marked paid. Use when you skip spending this month (e.g. bi‑monthly accrual): no cash out this month; the real payment shows in the month you pay.",
      paidZeroSuccess: "Expense settled at 0 and marked as paid.",
      actualAmountPlaceholder: "0.00",
    },
    expenseDetail: {
      screenTitle: "Expense Details",
      totalAmountLabel: "TOTAL AMOUNT",
      dateLabel: "Date",
      categoryLabel: "Category",
      scopeLabel: "Scope",
      paymentMethodLabel: "Payment method",
      notesLabel: "Notes",
      paidByLabel: "Paid by",
      recurringLabel: "Recurrence",
      proratedLabel: "Monthly prorated",
      splitsTitle: "COST DISTRIBUTION",
      splitPercent: "{{percent}} % contribution",
      markAsPaidCta: "Mark as paid",
      markAsUnpaidCta: "Mark as pending",
      markingPaid: "Marking…",
      markingUnpaid: "Undoing…",
      markPaidSuccess: "Expense marked as paid.",
      markUnpaidSuccess: "Expense marked as pending.",
      editCta: "Edit",
      noCategoryLabel: "No category",
      noNotesLabel: "No notes",
      confirmMarkPaid: "Confirm payment",
      confirmMarkPaidMessage: "Mark this expense as paid?",
      confirmMarkUnpaid: "Undo payment",
      confirmMarkUnpaidMessage: "Mark this expense as pending again?",
      errorMarkPaid: "Could not mark as paid. Please try again.",
      errorMarkUnpaid: "Could not undo payment. Please try again.",
      cycleLabel: "Cycle",
      projectionLabel: "Projection",
      perMonth: "mo",
      actualCostLabel: "Actual cost",
      realCostLabel: "Actual amount recorded",
      updateCostTitle: "Update real cost",
      updateCostCta: "Update cost",
      updateCostError: "Could not update cost. Please try again.",
      updateCostSuccess: "Actual cost updated.",
      receiptLabel: "Receipt",
      receiptAttached: "Receipt attached",
      receiptNotAttached: "No receipt attached",
      receiptViewCta: "View receipt",
      receiptAttachCta: "Attach receipt",
      receiptReplaceCta: "Replace receipt",
      receiptSourceTitle: "Attach receipt",
      receiptSourceMessage: "Choose how to attach the receipt.",
      receiptSourceCamera: "Take photo",
      receiptSourceGallery: "Choose from gallery",
      receiptSourceDocument: "Choose document",
      receiptUpdating: "Uploading receipt…",
      receiptUpdatedSuccess: "Receipt updated.",
      receiptUpdateError: "Could not update the receipt. Please try again.",
      deleteCta: "Delete expense",
      deleteConfirmTitle: "Delete expense",
      deleteConfirmMessage: "Delete this expense? This cannot be undone.",
      deleteError: "Could not delete the expense. Please try again.",
      deleteSuccess: "Expense deleted.",
      deleting: "Deleting…",
      freqMonthly: "Monthly",
      freqBimonthly: "Bimonthly",
      freqQuarterly: "Quarterly",
      freqSemiannual: "Semiannual",
      freqAnnual: "Annual",
      methodCard: "Card",
      methodCash: "Cash",
      methodAccount: "Account",
      budgetSetupBannerTitle: "No budget reference for this month",
      budgetSetupBannerBody:
        "This month has no category budget and no household monthly cap. Set them in Budget.",
      budgetSetupBannerCta: "Open budget",
    },
    expenseSeries: {
      screenTitle: "Recurring series",
    },
    houses: {
      screenTitle: "Houses & members",
      subtitle: "Pick an active home to see linked expenses and budget.",
      managementEyebrow: "Management",
      heroDescription:
        "Organize your properties and manage how expenses are distributed among housemates with precision.",
      sampleHouseName: "Main home",
      activeBadge: "Active",
      membersCta: "Members & invites",
      budgetHint: "Month budget at 68%",
      budgetHintLive: "Month progress: {{percent}}% · spent to date {{spent}}",
      empty: "You have no homes yet. Create one or accept an invite on the web.",
      emptyTitle: "No homes yet",
      emptyDescription:
        "Create your first home to sync expenses, budget, and vault with everyone in the household.",
      setActiveHint: "Tap the card to use this home across the app.",
      membersScreenTitle: "Home members",
      membersSubtitle: "People with access to this home and their roles.",
      memberRoleOwner: "Owner",
      memberRoleAdmin: "Admin",
      memberRoleMember: "Member",
      memberSince: "Since {{date}}",
      invitePendingTitle: "Pending invitations",
      inviteExpires: "Expires {{date}}",
      inviteExpired: "Expired",
      inviteEmpty: "No pending invitations.",
      membersEmpty: "No members in this home.",
      inviteCta: "Invite member",
      inviteComingSoon: "Invitations are managed from the web. Coming soon in the app.",
      createScreenTitle: "New home",
      createSubtitle: "Give your home a name to start tracking expenses.",
      nameLabel: "Home name",
      namePlaceholder: "E.g. Main home, García family…",
      createCta: "Create home",
      creating: "Creating…",
      createSuccess: "Home created successfully.",
      createError: "Could not create the home. Please try again.",
      financialProfileCta: "Financial profile",
      financingsCta: "Financings",
      financialProfileScreenTitle: "Financial profile",
      financialProfileSubtitle: "Reference balance, currency and monthly budget cap.",
      financialProfileFinancingsCta: "View home financings",
      openingBalanceLabel: "Opening balance (account)",
      openingBalancePlaceholder: "0.00",
      currencyLabel: "Currency (ISO 4217)",
      currencyPlaceholder: "EUR",
      monthlyBudgetLabel: "Monthly cap (optional)",
      monthlyBudgetPlaceholder: "0.00",
      saveProfileCta: "Save profile",
      savingProfile: "Saving…",
      saveProfileSuccess: "Financial profile updated.",
      saveProfileError: "Could not save. Please try again.",
      invalidAmount: "Enter a valid amount (0 or greater).",
      inviteEmailSection: "Invite by email",
      inviteRoleLabel: "Role",
      inviteEmailLabel: "Guest email",
      inviteEmailPlaceholder: "name@example.com",
      inviteSendCta: "Send invitation",
      inviteSending: "Sending…",
      inviteSentSuccess: "Invitation sent. The guest will receive an email.",
      inviteError: "Could not send invitation. Please try again.",
      revokeInviteCta: "Revoke",
      revokeInviteConfirm: "Revoke this invitation? The person won't be able to join with that link.",
      revokeInviteSuccess: "Invitation revoked.",
      revokeInviteError: "Could not revoke. Please try again.",
      residentsTitle: "Residents",
      manageAllCta: "Manage all",
      splitAllocationTitle: "Split allocation",
      allocatedBudgetLabel: "Allocated budget",
      perMonthSuffix: "/ mo",
      adjustSplitCta: "Adjust split",
      soloMemberHint: "Only you on this property.",
      inviteCompactCta: "Invite",
      inviteCardTitle: "Invite a member",
      inviteCardBody: "Share expenses easily by inviting other residents to your home.",
      inviteCardCta: "Go to members & invite",
      proTipTitle: "Saving tip",
      proTipBody:
        "Households with splits aligned to real usage often save more on utilities. Review your monthly budget regularly.",
      proTipLink: "View household budget",
      settingsA11y: "Home settings",
      useAsActiveCta: "Use as active home",
      setActiveSuccess: "Active home updated.",
      removeMemberCta: "Remove",
      removeMemberConfirm: "Remove this person from the household?",
      removeMemberSuccess: "Member removed from the household.",
      removeMemberError: "Could not remove the member. Please try again.",
      changeRoleCta: "Change role",
      changeRoleTitle: "New role",
      roleUpdatedSuccess: "Role updated.",
      roleUpdateError: "Could not change the role. Please try again.",
      inviteAdminOnly: "Only owners and admins can invite.",
    },
    budget: {
      screenTitle: "Prorated budget",
      subtitle: "Planned vs actual spend for the active home.",
      heroMainTitle: "Money needed",
      heroMainAccent: "this month",
      netBalanceLabel: "Estimated net balance",
      netBalanceMock: "€624.30",
      progressLabel: "Of monthly cap used",
      progressPercent: "{{percent}}%",
      rowCategory: "Category",
      rowBudgeted: "Planned",
      rowActual: "Actual",
      demoUtilitiesBudgeted: "€120",
      demoUtilitiesActual: "€89",
      demoInsuranceBudgeted: "€85",
      demoInsuranceActual: "€85",
      emptyRows: "No category budget plan exists for this month.",
      emptyPerformanceTitle: "No performance data yet",
      emptyPerformanceDescription:
        "Set planned amounts per category to compare with what you actually spend.",
      proratedTotalLabel: "Prorated total",
      moneyRequiredLabel: "Total required",
      remanentLabel: "Remainder {{amount}}",
      proratedInstallmentType: "Installment",
      proratedInstallmentMonthly: "Monthly",
      proratedItemTotal: "Total {{amount}}",
      proratedMoveHint: "Monthly amount reserved for annual payments.",
      adjustGoalsCta: "Adjust goals",
      fabEditBudget: "Edit budget",
      fabCreateBudget: "Create budget",
      performanceSectionTitle: "Budgeted vs actual",
      performanceSectionSubtitle: "Planned budget compared with actual spending.",
      performanceSectionPlanNote:
        "“Plan” is the amount you save when editing the monthly category budget; it is not filled automatically from the sum of your recurring charge forecasts. “Actual” sums expenses already paid in the month (uses recorded actual cost when set).",
      filtersCta: "Filters",
      exportPdfCta: "Export PDF",
      exportPdfComingSoon: "PDF export will be available soon.",
      editByCategoryTitle: "Category planning",
      editByCategorySubtitle: "Set the planned amount for each category this month.",
      createCategoryCta: "New category",
      createCategoryTitle: "Create category",
      createCategorySubtitle: "Add a category to use in expenses and budgets.",
      categoryCreatedSuccess: "Category created successfully.",
      deleteCategoryA11y: "Delete category {{name}}",
      deleteCategoryConfirmTitle: "Delete category",
      deleteCategoryConfirmMessage:
        "Are you sure you want to delete “{{name}}”? Expenses that used it will have no category, and related budget lines will be removed.",
      deleteCategoryCta: "Delete",
      deleteCategorySuccess: "Category deleted.",
      deleteCategoryError: "Could not delete the category. Please try again.",
    },
    analytics: {
      screenTitle: "Household insights",
      openFromBudget: "Insights & charts",
      backToBudget: "Budget",
      subtitle: "Last {{count}} months — paid vs planned and category signals.",
      empty: "No paid expenses in this period. Insights will appear when you start logging paid spending.",
      chartTitle: "By month",
      legendPaid: "Paid",
      legendPlanned: "Planned",
      legendAvg: "Avg paid",
      chartScrollHint:
        "Swipe the chart sideways to see every month; the most recent months are on the right.",
      chartYAxisA11y: "Vertical amount axis from zero to {{max}}.",
      peakTitle: "Peak month",
      peakBody: "{{month}} · {{amount}}",
      peakNone: "No data",
      avgTitle: "Avg paid / active month",
      avgHint: "Months with zero paid spending are excluded.",
      topCategories: "Top accumulated spending by category (last {{count}} months)",
      otherCategories: "Other categories: {{amount}}",
      overspendTitle: "Biggest cumulative overrun (last {{count}} months)",
      overspendBody:
        "“{{name}}”: {{amount}} over plan in months with a line, accumulated over the last {{count}} months.",
      reviewTitle: "Review (often over plan)",
      reviewHint: "≥80% of budgeted months were over plan (with enough history).",
      reviewEmpty: "None for this window.",
      reduceTitle: "Trim plan (often under)",
      reduceHint: "≥80% of budgeted months were under plan.",
      reduceEmpty: "None for this window.",
    },
    vault: {
      screenTitle: "Warranty vault",
      subtitle: "Warranties and assets with expiry dates.",
      searchPlaceholder: "Search the vault…",
      alertTitle: "Upcoming expiry",
      alertBody: "Fridge warranty expires in 45 days.",
      alertBodyLive: "Warranty for “{{name}}” is expiring soon. Review documents on the web.",
      itemFridge: "Fridge · kitchen",
      itemInsurance: "Home insurance",
      completionLabel: "Inventory complete",
      empty: "No items in this home’s vault.",
      emptyTitle: "Your vault is empty",
      emptyDescription:
        "Register appliances, furniture, and policies with warranty dates. We’ll remind you before they expire.",
      emptyFilterTitle: "Nothing in this category",
      emptyFilterDescription:
        "Try another tab or add an asset that fits this section.",
      detailScreenTitle: "Vault · Asset",
      detailBadge: "Record",
      detailTitle: "Asset details",
      detailSubtitle: "Original purchase information and warranty.",
      detailAssetTypeLabel: "Category",
      detailPurchaseDateLabel: "Purchase date",
      detailWarrantyEndsLabel: "Warranty ends",
      detailNotesLabel: "Notes",
      detailDocumentLabel: "Receipt",
      detailDocumentPresent: "Document attached",
      detailDocumentMissing: "No receipt",
      detailNoAssetType: "No category",
      detailNoPurchaseDate: "Not specified",
      detailNoWarrantyEnds: "No expiry",
      detailNoNotes: "No notes",
      detailEditCta: "Edit",
      detailWarrantyExpired: "Warranty expired",
      detailWarrantyActiveDays: "{{days}} days remaining",
      createScreenTitle: "Add to Vault",
      editScreenTitle: "Edit asset",
      saveSuccess: "Asset saved to vault.",
      editSuccess: "Asset updated successfully.",
      saveError: "Could not save the asset. Please try again.",
      deleteCta: "Delete asset",
      deleteConfirm: "Delete this asset from the vault? This action cannot be undone.",
      deleteSuccess: "Asset deleted from vault.",
      deleteError: "Could not delete. Please try again.",
      nameLabel: "Item name",
      namePlaceholder: "E.g. Washing machine, Home insurance…",
      assetTypeLabel: "Category",
      assetTypePlaceholder: "E.g. Appliance, Policy…",
      purchaseDateLabel: "Purchase date",
      warrantyEndsLabel: "Warranty expiry",
      notesLabel: "Notes",
      notesPlaceholder: "Contract details, references…",
      saveCta: "Save to Vault",
      saving: "Saving…",
      heroEyebrow: "Document storage",
      heroTitle: "Vault",
      heroDescription:
        "Securely manage your home guarantees and insurance policies. We’ll notify you before your protection expires.",
      criticalEyebrow: "Critical alert",
      criticalBodyUrgent:
        "Guarantee for “{{name}}” expires in {{days}} days. Renew or verify coverage now.",
      criticalBodyExpired: "The warranty for “{{name}}” has expired. Review or renew coverage.",
      criticalCtaDetails: "View details",
      criticalCtaExtend: "Extend plan",
      statTotalLabel: "Total assets",
      statDocsLabel: "With receipt",
      filterAll: "All items",
      sectionCount: "{{count}} items",
      cardPurchaseLabel: "Purchase date",
      cardExpiresLabel: "Expiry date",
      viewReceiptCta: "View receipt",
      registerNewCta: "Register new item",
      badgeExpired: "Expired",
      badgeEndsToday: "Ends today",
      badgeDaysLeft: "{{count}} days left",
      badgeMonthsLeft: "{{count}} mo left",
      badgeYearsLeft: "{{count}} yr left",
      badgeSafe: "Safe",
      badgeNoDate: "No date",
      formEyebrow: "Registration",
      formTitle: "New asset details",
      formSubtitle: "Secure your investment by providing the original purchase information.",
      uploadSectionLabel: "Ticket / invoice proof",
      uploadTitle: "Upload proof of purchase",
      uploadSubtitle: "Choose a file or a photo from your gallery",
      uploadTypesHint: "JPG, PNG, WebP, GIF or PDF · up to {{maxMb}} MB",
      uploadPickFilesCta: "Choose file",
      uploadPickGalleryCta: "Gallery",
      uploadUploading: "Uploading…",
      uploadAttachedHint: "Attached: {{fileName}}",
      uploadRemoveCta: "Remove",
      uploadRemoveA11y: "Remove attached document",
      uploadErrorMime: "File type not allowed. Use an image (JPG, PNG, WebP, GIF) or PDF.",
      uploadErrorSize: "File exceeds the maximum size ({{maxMb}} MB).",
      uploadPermissionDenied:
        "Photo library access denied. Enable it in your device settings.",
      uploadErrorGeneric: "Could not upload the file. Please try again.",
      uploadSuccessReady: "File ready. Save the asset to keep the receipt.",
      uploadSoon: "File upload will be available soon.",
      completionStatusLabel: "Vault completion status",
      saveDisclaimer:
        "By saving, you authorize secure cloud encryption of your purchase documents and warranty dates.",
      moreOptionsA11y: "More options",
    },
    journalFilters: {
      screenTitle: "Filters",
      badge: "Refine view",
      title: "Filters",
      subtitle: "Adjust criteria to find specific expenses.",
      categoriesSection: "Categories",
      membersSection: "Members",
      dateRangeSection: "Date range",
      paidStatusSection: "Payment status",
      scopeSection: "Scope",
      fromLabel: "From",
      toLabel: "To",
      paidOnly: "Paid only",
      unpaidOnly: "Unpaid only",
      allStatuses: "All",
      applyCta: "Apply filters",
      resetCta: "Reset",
      applying: "Applying…",
    },
    profile: {
      screenTitle: "Account",
      settingsItem: "Account settings",
      settingsItemSub: "Manage your personal data",
      notificationsItem: "Notifications",
      notificationsItemSub: "Alerts and activity updates",
      securityItem: "Privacy & Security",
      securityItemSub: "Password and permissions",
      helpItem: "Help & Support",
      helpItemSub: "FAQs and contact centre",
      signOutItem: "Sign out",
      signOutItemSub: "Securely sign out",
      deleteAccountItem: "Delete account",
      deleteAccountItemSub: "Permanently remove your data and access",
      deleteAccountConfirmTitle: "Delete account?",
      deleteAccountConfirmMessage:
        "Your user and personal data will be permanently deleted. This cannot be undone.",
      deleteAccountConfirmCta: "Delete account",
      deleteAccountError: "Could not delete the account. Please try again.",
      totalAssetsLabel: "Total assets",
      activeBudgetsLabel: "Months with budget",
      comingSoon: "Coming soon.",
    },
    settings: {
      hubTitle: "Account",
      hubSubtitle: "Manage your profile, security, and app preferences.",
      backToAccount: "Back to account",
      cardPersonalTitle: "Personal details",
      cardPersonalHint: "Name, email, and interface language.",
      cardPasswordTitle: "Password",
      cardPasswordHint: "Update your sign-in password.",
      cardSessionsTitle: "Devices and sessions",
      cardSessionsHint: "Active sessions on other browsers or devices.",
      cardPasskeysTitle: "Passkeys and biometrics",
      cardPasskeysHint: "Passkey pre-auth registration and local biometric access.",
      cardNotificationsTitle: "Notifications",
      cardNotificationsHint: "Push, alerts, and registered devices.",
      cardSupportTitle: "Help and support",
      cardSupportHint: "Recover access and help resources.",
      cardAppearanceTitle: "Appearance",
      cardAppearanceHint: "Light, dark, or match system settings.",
      avatarChangeA11y: "Change profile photo",
      avatarUploading: "Uploading photo…",
      clearAvatar: "Remove profile photo",
      avatarPickError: "Choose an image (JPG, PNG, WebP, or GIF).",
      avatarUploadError: "Could not update your photo. Please try again.",
      footerBrand: "HomeExpense",
      personalTitle: "Personal details",
      personalSubtitle: "Your email cannot be changed from the app.",
      emailLabel: "Email",
      emailHint: "To change your email, contact support.",
      nameField: "Display name",
      profileFormSectionTitle: "Profile and language",
      saveButton: "Save changes",
      saving: "Saving…",
      savedHint: "Changes saved.",
      appearanceTitle: "Appearance",
      appearanceSubtitle: "Choose how the app looks on your device.",
      appearanceLight: "Light",
      appearanceDark: "Dark",
      appearanceSystem: "System",
      appearanceCurrent: "Selected",
      sessionsTitle: "Devices and sessions",
      sessionsSubtitle: "Sessions with access to your account.",
      sessionsIntro:
        "You can sign out other devices. If you sign out this device, you will need to sign in again.",
      sessionsLoadError: "Could not load sessions.",
      sessionsEmpty: "No other sessions found.",
      sessionsSignOutOthers: "Sign out other sessions",
      sessionsSignOutDevice: "Sign out",
      sessionsThisDevice: "This device",
      sessionsCurrentBadge: "Current",
      sessionsLastActive: "Last active",
      sessionsStarted: "Signed in",
      sessionsExpires: "Expires",
      sessionsIp: "IP",
      sessionsUnknownUa: "Unknown client",
      sessionsSignOutDeviceConfirm: "Sign out on this device?",
      sessionsSignOutOthersConfirm: "Sign out all sessions except this one?",
      sessionsActionError: "Could not complete the action. Please try again.",
      passkeysTitle: "Passkeys and biometrics",
      passkeysSubtitle:
        "Manage fast unlock on this device and prepare pre-auth context for passkey registration.",
      passkeysBiometricTitle: "Verify biometric unlock",
      passkeysBiometricHint: "Test Face ID / Touch ID / fingerprint with your current session.",
      passkeysBiometricSuccess: "Biometric unlock validated successfully on this device.",
      passkeysBiometricError: "Could not validate biometrics. Please try again.",
      passkeysContextTitle: "Generate passkey pre-auth context",
      passkeysContextHint:
        "Generate a temporary signed context to continue passkey registration on compatible clients.",
      passkeysContextSuccess: "Passkey pre-auth context generated.",
      passkeysContextSuccessHint: "Expires in {{seconds}} seconds.",
      passkeysContextError: "Could not generate passkey context.",
      notificationsTitle: "Notifications",
      notificationsSubtitle: "Alert preferences and push registration on this device.",
      pushSectionTitle: "Push notifications",
      activatePush: "Enable or refresh push",
      pushWorking: "Push notifications are available on this device.",
      pushConfigMissing: "Firebase is not configured in the app.",
      pushPermissionDenied: "Notification permission denied. Enable it in system Settings.",
      pushRegisterSuccess: "Device registered for push notifications.",
      pushFailureNoSession: "Sign in to register this device for notifications.",
      pushFailurePermissionRequest: "Could not complete the notification permission request.",
      pushFailureIosRemote:
        "Could not enable remote notifications on this iPhone. Check Firebase and APNs setup.",
      pushFailureToken:
        "Could not get the notification token. Check Google Play services, connectivity, or Firebase configuration.",
      pushRegisterError: "Could not register the device for notifications.",
      prefsSectionTitle: "Preferences",
      prefPushMaster: "Enable push notifications",
      prefHouseInvites: "House and invitations",
      prefExpenses: "Expenses and payments",
      prefBudget: "Budget",
      prefReminders: "Reminders",
      prefScanDocs: "Scan and documents",
      prefDigests: "Digests",
      prefUpcomingCharges: "Charges due tomorrow (unpaid)",
      devicesSectionTitle: "Registered devices",
      noPushDevices: "No devices registered for push.",
      lastSeenLabel: "Last seen",
      devicePushLabel: "Push enabled",
      removeDevice: "Remove",
      removeDeviceConfirm: "Remove this device from the list?",
      platformWeb: "Web",
      platformIos: "iOS",
      platformAndroid: "Android",
      supportTitle: "Help and support",
      supportSubtitle: "Resources to recover access or get help.",
      supportIntro:
        "If you cannot access your account, you can request a password reset link.",
      supportForgotTitle: "Forgot your password?",
      supportForgotBody: "We will send a secure link to the email you provide.",
      supportForgotCta: "Request recovery link",
    },
    scan: {
      screenTitle: "AI scan",
      subtitle: "Capture a receipt to extract the amount automatically.",
      scanningHint: "Frame the document in the scan area.",
      openGallery: "Gallery",
      manualEntry: "Manual entry",
      recentTitle: "Recent scans",
      empty: "No receipt scans for this home yet.",
      emptyTitle: "No recent scans",
      emptyDescription:
        "Upload a photo of a receipt and we’ll extract the amount automatically with AI.",
      emptyCta: "Upload receipt",
      statusPending: "Pending",
      statusProcessing: "Processing",
      statusCompleted: "Completed",
      statusFailed: "Failed",
      uploading: "Uploading image…",
      registering: "Registering scan…",
      uploadSuccess: "Receipt received. We’ll process it in a few seconds.",
      pollHint: "Analysing receipt with AI…",
      createExpenseFromScan: "Create expense with this data",
      extractedTitle: "Detected data",
      extractedMerchant: "Merchant",
      extractedDate: "Date",
      extractedTotal: "Total",
      extractionErrorFallback: "Could not interpret the receipt.",
      permissionDenied: "Camera permission denied. Enable it in Settings.",
      permissionBlocked: "Permission blocked. Go to Settings > Privacy > Camera to enable it.",
    },
    addExpense: {
      createTitle: "New expense",
      editTitle: "Edit expense",
      saveSuccess: "Expense saved successfully.",
      editSuccess: "Expense updated successfully.",
      saveError: "Could not save the expense. Please try again.",
      invalidAmount: "Enter a valid amount.",
      invalidCurrency: "Enter a valid ISO currency code (3 letters, e.g. EUR).",
      titleOrCategoryRequired: "Add a title or select a category.",
      titleLabel: "Title",
      titlePlaceholder: "E.g. Weekly shop, Electricity…",
      amountLabel: "Amount",
      dateLabel: "Date",
      categoryLabel: "Category",
      categoryNone: "No category",
      paymentMethodLabel: "Payment method",
      paymentMethodNone: "Not specified",
      paymentMethodCard: "Card",
      paymentMethodCash: "Cash",
      paymentMethodAccount: "Account",
      paidByLabel: "Paid by",
      paidByDefault: "Household account",
      paidNowLabel: "Mark as paid now",
      notesLabel: "Notes",
      notesPlaceholder: "Details or references…",
      receiptLabel: "Receipt",
      receiptAttachCta: "Attach PDF or image",
      receiptAttachedHint: "File ready to save with the expense.",
      receiptClearA11y: "Remove receipt",
      receiptUploading: "Uploading…",
      receiptUploadError: "Could not upload the file. Please try again.",
      receiptLinkedExisting: "A receipt is already saved; you can replace or remove it.",
      isRecurringLabel: "Recurring expense",
      recurrenceKindLabel: "Recurrence type",
      recurrenceMonthlyFixed: "Fixed monthly",
      recurrenceSeries: "Custom series",
      frequencyLabel: "Frequency",
      freqMonthly: "Monthly",
      freqBimonthly: "Bimonthly",
      freqQuarterly: "Quarterly",
      freqSemiannual: "Semiannual",
      freqAnnual: "Annual",
      endsOnLabel: "End date",
      endsOnPlaceholder: "YYYY-MM-DD (optional)",
      proratedLabel: "Prorate amount",
      monthlyFixedHint: "{{count}} monthly installments will be created automatically.",
      submitCreate: "Create expense",
      submitCreateRecurring: "Create recurring series",
      submitSave: "Save changes",
      beforeSubmitSummaryTitle: "Summary before saving",
      summaryConcept: "Title",
      summaryUnnamed: "(untitled)",
      summaryTotalAmount: "Total amount",
      summaryInstallmentAmount: "Installment for this period (split)",
      summaryDate: "Date",
      summaryCategory: "Category",
      summaryPaymentMethod: "Payment method",
      summaryPaidBy: "Paid by",
      summarySharedSplit: "Split (this installment)",
      summaryScheduleTitle: "Journal charges (preview)",
      summaryMoreDates: "… and {{count}} more",
      enterAmountForSplit: "Enter a valid amount to see the split.",
      sharedPreviewRangeInvalid: "Check the series start and end dates.",
      sharedPreviewFinalizeRecurrence: "Complete recurrence (amount and dates) to see the split.",
      flows: {
        launcherTitle: "What do you want to do?",
        launcherSubtitle:
          "Choose a path based on the type of expense you want to log.",
        manualOnlyBanner:
          "Enter the key expense details: amount, date, and payment method.",
        intentQuickTitle: "Log a one-off expense",
        intentQuickDescription:
          "For a charge you already made: amount, category, and how you paid. Everything else only if you need it.",
        intentRecurringTitle: "Fixed monthly expense",
        intentRecurringDescription:
          "A charge that repeats (rent, HOA…). A short guided flow explains what you are setting up.",
        intentSeriesTitle: "Series with dates or proration",
        intentSeriesDescription:
          "Installments with an end date, different frequencies, or splitting a total across months. Review before saving.",
        quickExpandMore: "More options (title, notes, shared…)",
        repeatLastFlowCta: "Continue with: {{flowLabel}}",
        navNext: "Next",
        navBack: "Back",
        navReview: "Review",
        editStepA11y: "Edit step {{step}}",
        monthlyWizard: {
          step1Title: "What is this expense?",
          step1Help: "Title or category so you can recognize it in the journal every month.",
          step2Title: "How much do you pay per month?",
          step2Help: "A single monthly amount. We repeat it for each installment.",
          step3Title: "When does it start?",
          step3Help: "First charge date or the day you want it to appear in the journal.",
          step4Title: "How do you pay it?",
          step4Help: "Card, cash, or account—you record how the money leaves (all manual).",
          step5Title: "Is it shared at home?",
          step5Help: "Who pays and whether the expense is shared across members.",
          reviewTitle: "Your fixed expense will look like this",
          reviewHelp: "Check the details. You can go back and change any step.",
        },
        seriesWizard: {
          step1Title: "Title and amount",
          step1Help:
            "What this is and the amount for the mode you choose (per month or period total—set in the next steps).",
          step2Title: "How often it repeats",
          step2Help: "Monthly, quarterly, yearly… Frequency shapes how charges show in the journal.",
          step3Title: "Start and end",
          step3Help:
            "Start date and, if needed, last installment. With no end date, the series may stay open depending on rules.",
          step4Title: "Proration",
          step4Help:
            "If you split a total across installments, this explains the split. No bank link—you confirm amounts.",
          step5Title: "Split across people",
          step5Help: "Shared flag and who paid. You will see a preview when amount and dates are ready.",
          reviewTitle: "Series summary",
          reviewHelp: "Confirm installments and dates before creating the series.",
        },
      },
    },
    notifications: {
      screenTitle: "Notifications",
      empty: "No notifications",
      emptyHint: "Home updates and alerts will appear here.",
      markAllRead: "Mark all read",
      markAllReadConfirm: "Mark all notifications as read?",
      markAllReadSuccess: "All notifications marked as read.",
      markReadError: "Could not mark as read.",
    },
    pendingInvitations: {
      screenTitle: "Pending invitations",
      subtitle: "Homes you've been invited to. Accept to join and start sharing expenses.",
      empty: "No pending invitations",
      emptyHint: "When someone invites you to a home, it will appear here.",
      inviterLine: "Invited by: {{inviterName}}",
      expiresLabel: "Expires {{date}}",
      acceptCta: "Accept & join",
      accepting: "Joining…",
      acceptSuccess: "You joined the home successfully.",
      acceptError: "Could not accept the invitation. Please try again.",
    },
    financings: {
      screenTitle: "Financings",
      empty: "No financings",
      emptyHint: "Track loans and fixed instalments to monitor progress.",
      detailScreenTitle: "Financing detail",
      installmentLabel: "Instalment: {{amount}}",
      termProgressLabel: "{{percent}}% of term",
      contractStartLabel: "Contract start",
      endsOnLabel: "Last instalment",
      remainingLabel: "Estimated remaining: {{amount}}",
      openSeriesCta: "View recurring expenses",
      noSeries: "No linked recurring expenses",
      aiSummaryTitle: "AI Summary",
      deleteCta: "Delete financing",
      deleteConfirm: "Delete this financing? This action cannot be undone.",
      deleteSuccess: "Financing deleted.",
      deleteError: "Could not delete. Please try again.",
      remainingEstimatedShort: "Estimated remaining",
      addDocumentCta: "Add document",
      removeDocumentA11y: "Remove document",
      docMaxReached: "Maximum {{max}} documents.",
      docUploadError: "Could not upload the file. Please try again.",
      docTooLarge: "The file is too large.",
      docMimeNotAllowed: "File type not allowed.",
    },
    demo: {
      title: "Notifications",
      itemLabel: "Item {{index}}",
      listSubtitle: "GET /notifications (cursor). Sign in from Home to load data.",
      loadingMore: "Loading more…",
      sheetTitle: "Bottom sheet example",
      sheetBody:
        "This modal uses @gorhom/bottom-sheet. Use it for filters, short forms, or contextual actions.",
      sheetCloseHint: "Swipe down or tap outside to dismiss.",
      fetchError: "Could not load the list.",
      needSession: "No session. Go to Home and sign in with email and password.",
      emptyNotifications: "No notifications on this page.",
      notificationType: "Type: {{type}}",
    },
    auth: {
      screenTitle: "Sign in",
      signInTitle: "Welcome back",
      signInSubtitle: "Enter your credentials to access your ledger.",
      registerTitle: "Create account",
      registerSubtitle: "Start today with financial clarity at home.",
      registerScreenTitle: "Register",
      forgotScreenTitle: "Recover access",
      forgotBadge: "Security portal",
      forgotTitle: "Recover your financial access.",
      forgotTitleLead: "Recover your",
      forgotTitleAccent: "financial access.",
      forgotSubtitle: "Access",
      forgotBody:
        "Enter the email address for your HomeExpense account. We will send a secure link to reset your password.",
      forgotEmailLabel: "Registered email address",
      forgotPlaceholderEmail: "e.g. curator@estate.com",
      contactSupport: "Contact support",
      contactSupportHint: "In-app support is coming soon. Please use the help section on the web for now.",
      forgotDecorLetters: "LE",
      forgotDecorBrand: "Estate Ledger",
      forgotDecorVersion: "v. 4.0.2",
      subtitle: "Use the same account as on the web (email and password).",
      nameLabel: "Full name",
      namePlaceholder: "Your name",
      emailLabel: "Email address",
      emailPlaceholder: "name@example.com",
      passwordLabel: "Password",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirm",
      signInCta: "Sign in to Ledger",
      registerCta: "Create free account",
      sendRecoveryCta: "Send recovery link",
      signingIn: "Signing in…",
      registering: "Creating account…",
      sendingRecovery: "Sending…",
      signOut: "Sign out",
      goToSignIn: "Sign in",
      goToRegister: "Create account",
      goToForgot: "Forgot password?",
      backToSignIn: "Back to sign in",
      orContinueWith: "Or continue with",
      orRegisterWithEmail: "Or register with email",
      socialGoogle: "Google",
      socialApple: "Apple",
      signInWithApple: "Sign in with Apple",
      signUpWithApple: "Sign up with Apple",
      signInWithPasskey: "Sign in with Passkey",
      signUpWithPasskey: "Sign up with Passkey",
      continuingWithGoogle: "Connecting with Google…",
      continuingWithApple: "Connecting with Apple…",
      continuingWithPasskey: "Connecting with Passkey…",
      socialApplePending: "Apple sign-in on mobile will be available soon.",
      socialPasskey: "Passkey",
      errorGoogleFailed: "Could not sign in with Google. Please try again.",
      errorGoogleCancelled: "Google sign-in was cancelled.",
      errorGoogleConfigMissing:
        "Google Sign-In is not configured in this app. Check GOOGLE_WEB_CLIENT_ID.",
      errorAppleFailed: "Could not sign in with Apple. Please try again.",
      errorAppleCancelled: "Apple sign-in was cancelled.",
      errorAppleConfigMissing:
        "Apple Sign-In is not configured in this app. Check APPLE_CLIENT_ID and APPLE_APP_BUNDLE_IDENTIFIER.",
      errorPasskeyFailed: "Could not sign in with Passkey. Please try again.",
      errorPasskeyUnavailable: "Your device does not support Passkeys for this flow.",
      biometricPromptTitle: "Unlock HomeExpense",
      socialUnavailable: "Social sign-in on mobile will be available soon.",
      termsAgree: "I agree to the",
      termsLink: "Terms of Service",
      privacyLink: "Privacy Policy",
      termsConnector: "and",
      termsRequired: "You must accept the terms and privacy policy to continue.",
      alreadyHaveAccount: "Already have an account?",
      newHere: "New here?",
      sessionActive: "Signed in",
      forgotSentHint:
        "If an account exists for that email, you will receive a link shortly.",
      signIn: "Sign in",
      errorInvalidCredentials: "Incorrect email or password.",
      errorGeneric: "Could not sign in. Please try again.",
      errorPasswordMismatch: "Passwords do not match.",
      errorSignUpFailed: "Could not create the account. Please try again.",
      errorRecoveryFailed: "Could not send the link. Please try again.",
      registerCheckEmail:
        "Check your email to verify your account before signing in.",
      changePasswordScreenTitle: "Change password",
      changePasswordBadge: "SECURITY SETTINGS",
      changePasswordTitle: "Update your credentials",
      changePasswordSubtitle:
        "Keep your financial data safe with a strong, unique password.",
      currentPasswordLabel: "Current password",
      currentPasswordPlaceholder: "••••••••••••",
      newPasswordLabel: "New password",
      newPasswordPlaceholder: "••••••••••••",
      confirmNewPasswordLabel: "Confirm new password",
      changePasswordCta: "Update password",
      changingPassword: "Updating…",
      changePasswordSuccess: "Password updated",
      changePasswordSuccessHint:
        "Your password has been changed. You'll remain signed in on this device.",
      errorChangePasswordFailed:
        "Could not update password. Please try again.",
      errorChangePasswordWrong:
        "Current password is incorrect. Please check and try again.",
      passwordStrengthWeak: "Weak",
      passwordStrengthFair: "Fair",
      passwordStrengthGood: "Good",
      passwordStrengthStrong: "Strong",
      passwordStrengthLabel: "Security level",
      securityTipsTitle: "Security tips",
      securityTip1Title: "Uniqueness",
      securityTip1Body:
        "Don't reuse passwords from other financial services.",
      securityTip2Title: "Complexity",
      securityTip2Body:
        "Mix uppercase letters, numbers, and special symbols ($@!).",
      changePasswordSignOutHint:
        "Changing your password will sign you out of all other devices.",
    },
  },
  marketing: marketingEn,
  api: {
    errors: {
      validationFailed: "The submitted data is invalid",
      unauthorized: "Not authenticated",
      forbidden: "You do not have permission for this action",
      internal: "Internal server error",
      fallback: "Bad request",
      validation: {
        expectedString: "Field {{property}} must be a string",
        invalidUuid: "Field {{property}} must be a valid UUID",
        expectedInt: "Field {{property}} must be an integer",
        expectedBoolean: "Field {{property}} must be a boolean",
        expectedArray: "Field {{property}} must be an array",
        expectedObject: "Field {{property}} must be an object",
        invalidEnum: "Field {{property}} has an invalid value",
        min: "Field {{property}} is below the minimum allowed",
        max: "Field {{property}} exceeds the maximum allowed",
        pattern: "Field {{property}} has an invalid format",
        invalidField: "Field {{property}} is invalid",
      },
      houses: {
        nameRequired: "Name is required",
        createFailed: "Could not create the household",
        notFound: "Household not found",
        nameEmpty: "Name cannot be empty",
        userIdRequired: "userId is required",
        cannotAssignOwnerViaApi: "Owner role cannot be assigned through this endpoint",
        userAlreadyMember: "User is already a member",
        memberNotFound: "Member not found",
        atLeastOneOwner: "There must be at least one owner",
        defaultSplitBpsRange: "defaultSplitBps must be between 0 and 10000",
        cannotRemoveSoleOwner: "Cannot remove the only owner",
        financialProfileNotFound: "Financial profile not found",
        currencyIso: "Currency must be ISO 4217 (3 letters)",
        invitationEmailInvalid: "Invalid invitation email",
        invitationNotFound: "Invitation not found or no longer valid",
        invitationTokenInvalid: "Invalid invitation link",
        invitationEmailMismatch: "Sign in with the email address the invitation was sent to",
        invitationEmailFailed: "The invitation could not be sent. Check email configuration.",
        invitationUserMissing: "User account not found",
      },
      access: {
        notMember: "You are not a member of this household",
        adminRequired: "Administrator or owner role required",
        ownerOnly: "Only the owner can perform this action",
      },
      expenses: {
        userNotMember: "User {{userId}} is not a member of this household",
        notFound: "Expense not found",
        titleRequired: "Title is required",
        amountMinorInvalid: "amountMinor is invalid",
        categoryInvalidForHouse: "Invalid category for this household",
        categoryInvalid: "Invalid category",
        currencyIso: "Currency must be ISO 4217",
        currencyInvalid: "Invalid currency",
        createFailed: "Could not create the expense",
        titleEmpty: "Title cannot be empty",
        splitSumMustBe10000: "The sum of shareBps must be exactly 10000",
        splitDuplicateUser:
          "Splits cannot include the same user more than once (duplicate userId in splits)",
        splitShareBpsInvalid: "Invalid shareBps (must be between 0 and 10000)",
        recurrenceEndsBeforeStart: "End date must be on or after the start date",
        recurrenceNoOccurrences: "No installment dates fall within the selected range",
        proratedRequiresEndsOn:
          "With monthly frequency, prorating a total requires a series end date (or use bimonthly/quarterly… to split each period’s total into monthly lines without an end date)",
        seriesNotFound: "Recurring series not found",
        seriesFixedSplitsRequired:
          "Provide split shares for a fixed-split series",
        seriesSplitsOnlyForFixed:
          "Splits can only be sent when the series uses fixed split mode",
        seriesEndsBeforeAnchor:
          "End date cannot be before the series start date",
        seriesEndedWithFutureEndsOn:
          "To shorten the series in the future, only change the end date without marking the series as ended.",
      },
      financings: {
        notFound: "Financing not found",
        titleRequired: "Title is required",
        installmentInvalid: "Monthly installment is invalid",
        contractStartInvalid: "Contract start date is invalid",
        endsOnInvalid: "End date is invalid",
        endsBeforeContract: "End date cannot be before contract start",
        noFutureInstallments:
          "There are no future installments from today with those dates. Check contract start and end.",
        aiRequiresDocuments: "AI analysis requires at least one document",
        documentStorageKeyRequired: "Each document must have a storage key",
        summarizationFailed: "Could not generate the contract summary with AI",
        createFailed: "Could not save the financing",
      },
      budgets: {
        yearMonthFormat: "yearMonth must be YYYY-MM",
        plannedAmountInvalid: "plannedAmountMinor is invalid",
        categoryNotInHouse: "Category {{categoryId}} does not belong to this household",
        categoryNotFound: "Category not found",
      },
      vault: {
        notFound: "Item not found",
        nameRequired: "Name is required",
        nameEmpty: "Name cannot be empty",
        assetTypeInvalid:
          "Invalid category. Use one of: appliances, electronics, furniture, vehicles, other.",
      },
      receiptScans: {
        notFound: "Receipt scan not found",
        imageKeyRequired: "imageStorageKey is required",
        statusInvalid: "Invalid status",
        dailyLimitExceeded:
          "You have reached the daily AI scan limit ({{limit}}). Try again tomorrow.",
        extractionFailed: "Could not read the receipt. Try a sharper photo.",
        extractionDisabled:
          "AI extraction is not configured on the server. Contact the administrator.",
        imageTooLarge: "The image is too large to process with AI.",
        unsupportedMedia: "File format not supported for this scan.",
      },
      categories: {
        nameRequired: "Name is required",
        notFound: "Category not found",
        nameEmpty: "Name cannot be empty",
      },
      dashboard: {
        yearMonthInvalid: "yearMonth is invalid",
        yearMonthFormat: "yearMonth must be YYYY-MM",
      },
      notifications: {
        notFound: "Notification not found",
        deviceNotFound: "Device not found",
      },
      storage: {
        fileRequired: "A file is required",
        mimeNotAllowed: "File type not allowed: {{mime}}",
        fileTooLarge: "File too large. Maximum {{maxMb}} MB",
        keyRequired: "Storage object key is required",
        invalidKey: "Invalid or disallowed storage object key",
      },
      account: {
        userNotFound: "User not found",
        deletionBlockedReferences:
          "Account deletion could not finish because of pending data references. Please contact support.",
      },
      publicContact: {
        sendFailed: "We could not send your message. Please try again later.",
        serviceUnavailable:
          "Message delivery is not available right now (email service not configured or unavailable).",
      },
    },
  },
};

// Exportar también como default para next-intl
export default en;
